import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import * as XLSX from "xlsx";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { filterDimensionsByCourse } from './utils/dimensionFilters';
import { getCourseOrderIndex } from './utils/courseUtils';
import { studentIdsMatch, findStudentById } from './utils/studentIdUtils';
import { 
  FaSave, FaFileExcel, FaFilePdf, FaUsers, FaSignOutAlt, FaSearch, FaQuestionCircle, 
  FaInfoCircle, FaChevronRight, FaChevronLeft, FaChevronDown, FaBrain, FaComments, FaRunning, 
  FaPalette, FaBook, FaTree, FaUserFriends, FaCalculator, FaLanguage, FaPray, 
  FaChartBar, FaPen, FaEllipsisV, FaHome, FaBullseye, FaLock, FaLockOpen, FaSync, FaBell, 
  FaTimes, FaRocket, FaSyncAlt, FaLightbulb, FaCog, FaEnvelope, FaPhone, FaGlobe, FaChartPie, FaUserShield, FaWifi, FaGraduationCap, FaBuilding, FaChartLine, FaSun, FaMoon
} from 'react-icons/fa';
import { IconType } from 'react-icons';
import schoolIllustration from './assets/images/school-illustration.png';
import SaveProgressModal from './components/SaveProgressModal';
import { tokenManager } from './utils/tokenManager';
import { autoconcienciaVoluntadDimension } from './components/AutoconcienciaVoluntad';
import { habilidadesPensamientoDimension } from './components/HabilidadesPensamiento';
import { habilidadesComunicacionalesDimension } from './components/HabilidadesComunicacionales';
import { corporalidadMovimientoDimension } from './components/CorporalidadyMovimiento';
import { lenguajesArtisticosDimension } from './components/LenguajeArtistico';
import { motivacionAprendizajeDimension } from './components/Motivacionaprendizaje';
import { encuentroNaturalezaDimension } from './components/Encuentronaturaleza';
import { habilidadesSocialesDimension } from './components/Habilidadessociales';
import { pensamientoMatematicosDimension } from './components/Pensamientomatematico';
import { lenguaInglesaDimension } from './components/LenguaInglesa';
import { amorDiosDimension } from './components/AmordeDios';
// @ts-ignore
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { SocketProvider, SocketContextType } from './contexts/SocketContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import useSocketContext from './hooks/useSocketContext';
import AutoconcienciaVoluntad from './components/AutoconcienciaVoluntad';
import { HabilidadesPensamiento } from './components/HabilidadesPensamiento';
import HabilidadesComunicacionales from './components/HabilidadesComunicacionales';
import { CorporalidadMovimiento } from './components/CorporalidadyMovimiento';
import LenguajeArtistico from './components/LenguajeArtistico';
import { MotivacionAprendizaje } from './components/Motivacionaprendizaje';
import EncuentroNaturaleza from './components/Encuentronaturaleza';
import HabilidadesSociales from './components/Habilidadessociales';
import PensamientoMatematico from './components/Pensamientomatematico';
import LenguaInglesa from './components/LenguaInglesa';
import AmorDios from './components/AmordeDios';
import DescripcionCualitativa from './components/DescripcionCualitativa';
import Dashboard from './components/Dashboard';
import SimplifiedAnalytics from './components/SimplifiedAnalytics'; // COMPONENTE ACTIVO DE ANALYTICS
import UserManagement from './components/UserManagement';
import FormulariosWrapper from './components/FormulariosWrapper';
import VisualizacionResultados from './components/VisualizacionResultados';
import AdminDashboard from './components/admin/AdminDashboard';
import SimpleTutorialSection from './components/SimpleTutorialSection';
import LandingPage from './components/LandingPage';
import { Student, Dimension, Skill, SkillLevel } from './types';
import { API_BASE_URL } from './utils/apiConfig';

// Correo del administrador del sistema (debe coincidir con SYSTEM_ADMIN_EMAIL del backend). Sin definir, no hay admin de sistema.
const SYSTEM_ADMIN_EMAIL = process.env.REACT_APP_SYSTEM_ADMIN_EMAIL || 'sin-administrador-de-sistema';

// Declaración global para TypeScript
declare global {
  interface Window {
    startTutorial?: () => void;
  }
}

// Import imágenes desde assets para producción
const logoImage = '/logocolegiosinnotas.png';

// Función utilitaria para obtener el logo correcto según el tenant
const getTenantLogo = () => {
  // PRIMERO: Intentar obtener datos de organización desde localStorage
  const organizationData = localStorage.getItem('organizationData');
  if (organizationData) {
    try {
      const orgData = JSON.parse(organizationData);
      
      // PRIORIDAD 1: Logo en base64 (subido directamente por el admin)
      if (orgData.branding?.logoBase64) {
        console.log('🖼️ ✅ Logo base64 encontrado para tenant:', orgData.tenantKey);
        return orgData.branding.logoBase64;
      }
      
      // PRIORIDAD 2: Logo URL configurado
      if (orgData.branding?.logoUrl) {
        console.log('🖼️ Logo URL desde organizationData:', orgData.branding.logoUrl);
        // Si ya es una URL absoluta o base64, usarla directamente
        if (orgData.branding.logoUrl.startsWith('http') || orgData.branding.logoUrl.startsWith('data:')) {
          return orgData.branding.logoUrl;
        }
        return `${window.location.origin}${orgData.branding.logoUrl}`;
      }
    } catch (e) {
      console.error('Error parseando organizationData:', e);
    }
  }

  // FALLBACK: Intentar por tenantKey
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      const tenantKey = user.tenantKey || localStorage.getItem('tenantKey');
      
      // Para Colegio Puerto Varas usar logo local
      if (tenantKey === 'cpv_cl') {
        return `${window.location.origin}/logocolegiosinnotas.png`;
      }
    } catch (e) {
      console.error('Error parsing userData:', e);
    }
  }
  
  // ÚLTIMO FALLBACK
  return `${window.location.origin}/logocolegiosinnotas.png`;
};

// 🖼️ Función utilitaria para cargar imágenes como base64 para jsPDF
const loadImageAsBase64 = async (imagePath: string): Promise<string> => {
  // Si ya es base64, devolverlo directamente
  if (imagePath.startsWith('data:image')) {
    return imagePath;
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Para evitar problemas de CORS
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('No se pudo obtener contexto del canvas'));
          return;
        }
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Convertir a base64
        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      } catch (error) {
        console.error('Error procesando imagen:', error);
        reject(error);
      }
    };
    
    img.onerror = () => {
      console.error('Error cargando imagen:', imagePath);
      reject(new Error(`No se pudo cargar la imagen: ${imagePath}`));
    };
    
    img.src = imagePath;
  });
};

// Función utilitaria para obtener colores de branding personalizados
const getTenantBrandingColors = () => {
  // Colores por defecto (CPV)
  const defaultColors = {
    primary: { r: 25, g: 44, b: 84 }, // Azul marino de CPV
    secondary: { r: 246, g: 206, b: 30 }, // Amarillo dorado de CPV
    useBranding: false
  };

  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      const tenantKey = user.tenantKey || localStorage.getItem('tenantKey');
      
      // Para Colegio Puerto Varas mantener colores originales
      if (tenantKey === 'cpv_cl') {
        return defaultColors;
      }
      
      // Para otras organizaciones, obtener colores personalizados
      const organizationData = localStorage.getItem('organizationData');
      if (organizationData) {
        const orgData = JSON.parse(organizationData);
        if (orgData.branding?.primaryColor && orgData.branding?.secondaryColor) {
          return {
            primary: hexToRgbApp(orgData.branding.primaryColor) || defaultColors.primary,
            secondary: hexToRgbApp(orgData.branding.secondaryColor) || defaultColors.secondary,
            useBranding: true
          };
        }
      }
    }
  } catch (e) {
    console.error('Error obteniendo colores de branding:', e);
  }
  
  return defaultColors;
};

// Función utilitaria para convertir hex a RGB (App)
const hexToRgbApp = (hex: string) => {
  // Remover el # si existe
  const cleanHex = hex.replace('#', '');
  
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      return { r, g, b };
    }
  } else if (cleanHex.length === 3) {
    const r = parseInt(cleanHex.substring(0, 1) + cleanHex.substring(0, 1), 16);
    const g = parseInt(cleanHex.substring(1, 2) + cleanHex.substring(1, 2), 16);
    const b = parseInt(cleanHex.substring(2, 3) + cleanHex.substring(2, 3), 16);
    
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      return { r, g, b };
    }
  }
  
  return null;
};

// Interfaces para información de docentes (App)
interface TeacherInfoApp {
  spanishTeacher: string;
  englishTeacher: string;
}

// Cache global para docentes en App
let teachersCacheApp: Array<{ firstName: string; lastName: string; allowedCourses: string[]; role: string }> | null = null;
let teachersCacheTimestampApp = 0;
const CACHE_DURATION_APP = 5 * 60 * 1000; // 5 minutos

// Función para obtener docentes desde la API (App)
const fetchTeachersForApp = async (): Promise<Array<{ firstName: string; lastName: string; allowedCourses: string[]; role: string }>> => {
  try {
    // Verificar cache
    if (teachersCacheApp && Date.now() - teachersCacheTimestampApp < CACHE_DURATION_APP) {
      console.log('📋 Usando cache de docentes (App)');
      return teachersCacheApp;
    }

    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.warn('⚠️ No hay token de autenticación para obtener docentes (App)');
      return [];
    }

    const response = await fetch(`${API_BASE_URL}/api/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (result.success && result.data) {
      // Filtrar solo docentes (teachers)
      const teachers = result.data.filter((user: any) => user.role === 'teacher');
      
      // Actualizar cache
      teachersCacheApp = teachers.map((teacher: any) => ({
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        allowedCourses: teacher.allowedCourses || [],
        role: teacher.role
      }));
      teachersCacheTimestampApp = Date.now();
      
      console.log(`👨‍🏫 Docentes obtenidos (App): ${teachersCacheApp.length}`);
      return teachersCacheApp;
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error obteniendo docentes (App):', error);
    return [];
  }
};

const getTeacherInfo = async (course: string): Promise<TeacherInfoApp | undefined> => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.warn('⚠️ No hay token de autenticación para obtener docentes de informes');
      return getTeacherInfoHardcodedApp(course);
    }

    // 🆕 Intentar obtener docentes desde la API de teacher-reports (MongoDB)
    const response = await fetch(`${API_BASE_URL}/api/teacher-reports/settings/${encodeURIComponent(course)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data) {
        console.log(`👨‍🏫 Docentes obtenidos de MongoDB para ${course}:`, result.data);
        return {
          spanishTeacher: result.data.spanishTeacher,
          englishTeacher: result.data.englishTeacher || result.data.spanishTeacher
        };
      }
    }

    // Si no se encuentra en MongoDB, usar fallback hardcoded
    console.log(`⚠️ No se encontró configuración de docentes en MongoDB para: ${course}, usando fallback`);
    return getTeacherInfoHardcodedApp(course);
    
  } catch (error) {
    console.error('❌ Error obteniendo información de docentes de informes:', error);
    console.log('🔄 Usando datos hardcodeados como fallback');
    return getTeacherInfoHardcodedApp(course);
  }
};

// Función fallback con datos actualizados 2025 para CPV (App)
const getTeacherInfoHardcodedApp = (course: string): TeacherInfoApp | undefined => {
  const teachersInfoCurrent = [
    // Nivel Medio Mayor (basado en docentesporcurso.md)
    { course: "MMA", spanishTeacher: "Josefa Crovetto", englishTeacher: "Valentina Thienemann" },
    { course: "MMB", spanishTeacher: "Rossana Díaz", englishTeacher: "Nicole Larrea" },
    { course: "MMC", spanishTeacher: "Bernardita Suazo", englishTeacher: "María Jesús Valdivieso" },
    { course: "MMD", spanishTeacher: "María Jesús Jerez", englishTeacher: "Francisca Portaluppi" },
    // Prekínder
    { course: "PKA", spanishTeacher: "Camila Oyarzo", englishTeacher: "Teresa Echeverría" },
    { course: "PKB", spanishTeacher: "Isabel Rebolledo", englishTeacher: "Pilar Plaza de los Reyes" },
    { course: "PKC", spanishTeacher: "Carola Meersohn", englishTeacher: "Marcela Moreno" },
    // Kínder
    { course: "KA", spanishTeacher: "Magdalena Undurraga", englishTeacher: "Camila Álvarez" },
    { course: "KB", spanishTeacher: "Constanza Moguillansky", englishTeacher: "Carolina Krause" },
    { course: "KC", spanishTeacher: "Isidora Barrientos", englishTeacher: "Camila Mejías" },
    // Primero Básico
    { course: "1A", spanishTeacher: "Paula Larraín", englishTeacher: "Josefina Durán" },
    { course: "1B", spanishTeacher: "Danitza Munjin", englishTeacher: "Rebeca Pizarro" },
    { course: "1C", spanishTeacher: "Tamara Torres", englishTeacher: "María Carolina Alfaro" },
    // Segundo Básico
    { course: "2A", spanishTeacher: "Dolores Ugalde", englishTeacher: "Daniela Herrera" },
    { course: "2B", spanishTeacher: "Magdalena Izquierdo", englishTeacher: "María Luisa Gómez" },
    { course: "2C", spanishTeacher: "Soledad Parada", englishTeacher: "Magdalena Latorre" },
    // Tercero Básico
    { course: "3A", spanishTeacher: "Bernardita Montero", englishTeacher: "Bernardita Montero" },
    { course: "3B", spanishTeacher: "Macarena Gutiérrez", englishTeacher: "Macarena Gutiérrez" },
    { course: "3C", spanishTeacher: "Javiera Montero", englishTeacher: "Javiera Montero" },
    // Cuarto Básico (NUEVO)
    { course: "4A", spanishTeacher: "Claudia Uribe", englishTeacher: "Claudia Uribe" },
    { course: "4B", spanishTeacher: "Alejandra Ceza", englishTeacher: "Alejandra Ceza" },
    { course: "4C", spanishTeacher: "Andrea Infante", englishTeacher: "Andrea Infante" },
    // Quinto Básico (NUEVO)
    { course: "5A", spanishTeacher: "Sara Barra", englishTeacher: "Sara Barra" },
    { course: "5B", spanishTeacher: "Gloria Rivera", englishTeacher: "Gloria Rivera" },
    { course: "5C", spanishTeacher: "Daniela Sandoval", englishTeacher: "Daniela Sandoval" }
  ];

  const courseMap: { [key: string]: string } = {
    // Variaciones de Medio Mayor
    'Nivel medio mayor A': 'MMA',
    'Nivel medio mayor B': 'MMB',
    'Nivel medio mayor C': 'MMC',
    'Nivel medio mayor D': 'MMD',
    'Medio Mayor A': 'MMA',
    'Medio Mayor B': 'MMB',
    'Medio Mayor C': 'MMC',
    'Medio Mayor D': 'MMD',
    // Prekínder
    'Prekínder A': 'PKA',
    'Prekínder B': 'PKB',
    'Prekínder C': 'PKC',
    'Pre Kinder A': 'PKA',
    'Pre Kinder B': 'PKB',
    'Pre Kinder C': 'PKC',
    // Kínder
    'Kínder A': 'KA',
    'Kínder B': 'KB',
    'Kínder C': 'KC',
    'Kinder A': 'KA',
    'Kinder B': 'KB',
    'Kinder C': 'KC',
    // Básico
    'Primero básico A': '1A',
    'Primero básico B': '1B',
    'Primero básico C': '1C',
    '1° básico A': '1A',
    '1° básico B': '1B',
    '1° básico C': '1C',
    'Segundo básico A': '2A',
    'Segundo básico B': '2B',
    'Segundo básico C': '2C',
    '2° básico A': '2A',
    '2° básico B': '2B',
    '2° básico C': '2C',
    'Tercero básico A': '3A',
    'Tercero básico B': '3B',
    'Tercero básico C': '3C',
    '3° básico A': '3A',
    '3° básico B': '3B',
    '3° básico C': '3C',
    'Cuarto básico A': '4A',
    'Cuarto básico B': '4B',
    'Cuarto básico C': '4C',
    '4° básico A': '4A',
    '4° básico B': '4B',
    '4° básico C': '4C',
    'Quinto básico A': '5A',
    'Quinto básico B': '5B',
    'Quinto básico C': '5C',
    '5° básico A': '5A',
    '5° básico B': '5B',
    '5° básico C': '5C'
  };

  const shortCourse = courseMap[course] || course;
  return teachersInfoCurrent.find(t => t.course === shortCourse);
};

// HTTP-only coordination - using dedicated SocketContext from contexts/

// SocketContext is now imported from contexts/SocketContext

// Definimos las interfaces para los estudiantes, dimensiones y habilidades
export interface DimensionSummaryProps {
  student: Student;
  dimensionId: number;
}

interface Props {
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number) => void;
  viewMode: "student" | "skill";
  handleDescriptionChange: (studentId: number | string, value: string) => void;
  exportToExcel: (data?: any[]) => void;
  exportToPDF: (student: Student) => void;
  exportPDFMassive: (students: Student[]) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}

// Definir COLORS y CustomTooltip fuera del componente
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE'];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border">
        <p className="label">{`${label} : ${payload[0].value}`}</p>
        <p className="intro">{getAgeRangeFromLevel(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};


// Función auxiliar para obtener el rango de edad a partir del nivel
const getAgeRangeFromLevel = (level: number) => {
  switch (Math.floor(level)) {
    case 3: return "3-4 Años";
    case 4: return "4-5 Años";
    case 5: return "5-6 Años";
    case 6: return "6-7 Años";
    case 7: return "7-8 Años";
    default: return "Desconocido";
  }
};


// Función utilitaria robusta para parsear fechas en múltiples formatos
const parseDate = (birthDate: string): Date | null => {
  if (!birthDate) return null;
  
  const dateStr = birthDate.trim();
  
  // Formato YYYY-MM-DD
  if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) return date;
  }
  
  // Formato DD-MM-YYYY o DD-MM-YY
  if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{2,4}$/)) {
    const [day, month, yearStr] = dateStr.split('-').map(num => parseInt(num));
    
    // Convertir año de 2 dígitos a 4 dígitos
    let year = yearStr;
    if (yearStr < 100) {
      // Si el año es <= 25, asume que es 20XX, si es > 25, asume 19XX
      year = yearStr <= 25 ? 2000 + yearStr : 1900 + yearStr;
    }
    
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime()) && date.getFullYear() === year) return date;
  }
  
  // Formato MM/DD/YYYY o similar
  const fallbackDate = new Date(dateStr);
  if (!isNaN(fallbackDate.getTime())) return fallbackDate;
  
  return null;
};

// Función para normalizar IDs eliminando guiones pero manteniendo K
const normalizeStudentId = (id: string | number): string | number => {
  if (typeof id === 'string') {
    // Eliminar guiones pero mantener K
    const normalized = id.replace(/-/g, '');
    // Si es un número puro, convertir a number
    if (/^\d+$/.test(normalized)) {
      return Number(normalized);
    }
    // Si tiene K, mantener como string
    return normalized;
  }
  return id;
};

// Función calculateAge mejorada que devuelve number
const calculateAge = (birthDate: string): number => {
  const birth = parseDate(birthDate);
  if (!birth) return 0;
  
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  // Validar que la edad sea razonable
  if (age < 0 || age > 120) {
    console.warn(`⚠️ Edad calculada inválida: ${age} años para fecha: ${birthDate}`);
    return 0;
  }
  
  return age;
};


// Mover la definición de dimensionsTemplate antes de DimensionSummary
export const dimensionsTemplate: Dimension[] = [
  autoconcienciaVoluntadDimension,
  habilidadesPensamientoDimension,
  habilidadesComunicacionalesDimension,
  corporalidadMovimientoDimension,
  lenguajesArtisticosDimension,
  motivacionAprendizajeDimension,
  encuentroNaturalezaDimension,
  habilidadesSocialesDimension,
  pensamientoMatematicosDimension,
  lenguaInglesaDimension,
  amorDiosDimension
];

// Ahora DimensionSummary puede usar dimensionsTemplate
const DimensionSummary = ({ student, dimensionId }: { student: Student, dimensionId: number }) => {
  const dimension = dimensionsTemplate.find((d: Dimension) => d.id === dimensionId);
  if (!dimension) return null;

  // Calcular el porcentaje logrado usando skillLevels
  const skillLevelsForDimension = student.skillLevels.filter(
    sl => sl.dimensionId === dimensionId
  );

  const percentageAchieved = skillLevelsForDimension.length > 0
    ? (skillLevelsForDimension.reduce((sum, sl) => 
        sum + (sl.level !== null ? sl.level + 1 : 0), 0) / 
      (dimension.skills.length * dimension.skills[0].levels.length)) * 100
    : 0;

  const age = calculateAge(student.birthDate);
  
  const data = [{
    name: dimension.name,
    percentageAchieved: percentageAchieved,
    expectedLevel: calculateExpectedLevel(age),
  }];

  const lastYearPercentage = calculateLastYearPercentage(age);

  return (
    <div id={`chart-${dimensionId}`} className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h3 className="text-xl font-bold mb-4">Resumen de {dimension.name}</h3>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{
            top: 50,
            right: 50,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis
            type="number"
            domain={[0, 110]}
            ticks={[20, 60, 100]}  // Reducido a solo tres puntos
            tickFormatter={(tick) => {
              if (tick === 20) return "Hacia los 4 años";
              if (tick === 60) return "Hacia los 6 años";
              if (tick === 100) return "Hacia los 8 años";
              return "";
            }}
            orientation="top"
            axisLine={false}
            tickLine={false}
            tick={{ fontWeight: 'bold', fontSize: 20 }}
            interval={0}  // Asegura que se muestren todas las etiquetas
          />
          <YAxis 
            dataKey="name" 
            type="category" 
            width={150}
            tick={(props) => {
              const { x, y, payload } = props;
              const words = payload.value.split(' ');
              return (
                <g transform={`translate(${x},${y})`}>
                  <text x={-10} y={0} textAnchor="end" fill="#666" fontSize={20}>
                    {words.map((word: string, index: number) => (
                      <tspan x={-10} dy={index ? 20 : 0} key={index}>
                        {word}
                      </tspan>
                    ))}
                  </text>
                </g>
              );
            }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top" 
            height={36}
            payload={[
              { value: 'Nivel habilidad logrado', type: 'rect', color: '#8884d8' },
              { value: 'Nivel habilidad esperado', type: 'rect', color: '#ffc658' },
              { value: 'Nivel habilidad logrado año previo', type: 'line', color: '#6B7280' }
            ]}
          />
          <Bar dataKey="percentageAchieved" name="Nivel habilidad logrado" fill="#8884d8" radius={[0, 10, 10, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
          <Bar dataKey="expectedLevel" name="Nivel habilidad esperado" fill="#ffc658" radius={[0, 10, 10, 0]} />
          <ReferenceLine x={lastYearPercentage} stroke="#6B7280" strokeWidth={6} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};


// Actualizar la función calculatePercentageAchieved
const calculatePercentageAchieved = (dimension: Dimension, skillLevels: SkillLevel[] = []): number => {
  const dimensionSkillLevels = skillLevels.filter(sl => sl.dimensionId === dimension.id);
  
  if (dimensionSkillLevels.length === 0) return 0;
  
  const totalPossibleLevels = dimension.skills.length * dimension.skills[0].levels.length;
  const achievedLevels = dimensionSkillLevels.reduce((sum, sl) => 
    sum + (sl.level !== null ? sl.level + 1 : 0), 0);
  
  return (achievedLevels / totalPossibleLevels) * 100;
};


const calculateExpectedLevel = (age: number) => {
  // Mapeo correcto edad → porcentaje esperado
  const ageToPercentageMap: { [key: number]: number } = {
    3: 20,   // Medio Mayor - 20%
    4: 40,   // Pre Kinder - 40%
    5: 60,   // Kinder - 60%
    6: 80,   // 1° Básico - 80%
    7: 100,  // 2° Básico - 100%
    8: 100,  // 3° Básico - 100%
    9: 100,  // 4° Básico - 100%
    10: 100, // 5° Básico - 100%
    11: 100, // 6° Básico - 100%
    12: 100  // 6° Básico (máximo) - 100%
  };
  
  return ageToPercentageMap[age] ?? Math.min((age - 2) * 20, 100);
};

const calculateLastYearPercentage = (age: number) => {
  // Calcular el porcentaje esperado para la edad anterior
  const previousAge = age - 1;
  
  const ageToPercentageMap: { [key: number]: number } = {
    3: 20,   // Medio Mayor - 20%
    4: 40,   // Pre Kinder - 40%
    5: 60,   // Kinder - 60%
    6: 80,   // 1° Básico - 80%
    7: 100,  // 2° Básico - 100%
    8: 100,  // 3° Básico - 100%
    9: 100,  // 4° Básico - 100%
    10: 100, // 5° Básico - 100%
    11: 100, // 6° Básico - 100%
    12: 100  // 6° Básico (máximo) - 100%
  };
  
  // Si la edad anterior es menor a 3, retornar 0
  if (previousAge < 3) return 0;
  
  return ageToPercentageMap[previousAge] ?? Math.min((previousAge - 2) * 20, 100);
};

// Calculate previous year level using historical data if available
// Falls back to age-based calculation if no historical data exists
const calculatePreviousYearLevelForStudent = (
  student: Student,
  dimensionId: number,
  skillId: number,
  fallbackAge?: number
): number | null => {
  // Check if student has previousYearLevels data
  const previousYearLevels = (student as any).previousYearLevels;
  
  if (previousYearLevels && Array.isArray(previousYearLevels) && previousYearLevels.length > 0) {
    // Find the specific skill's previous year level
    const historicalLevel = previousYearLevels.find(
      (p: any) => p.dimensionId === dimensionId && p.skillId === skillId
    );
    
    if (historicalLevel && historicalLevel.level !== null && historicalLevel.level !== undefined) {
      return historicalLevel.level;
    }
  }
  
  // No historical data found - return null to let caller use fallback calculation
  return null;
};

// Calculate the overall previous year percentage for a student using historical data
const calculateHistoricalPreviousYearPercentage = (
  student: Student,
  totalSkillsCount: number = 438
): number | null => {
  const previousYearLevels = (student as any).previousYearLevels;
  
  if (!previousYearLevels || !Array.isArray(previousYearLevels) || previousYearLevels.length === 0) {
    return null; // No historical data
  }
  
  // Count completed skills from previous year
  const completedSkills = previousYearLevels.filter(
    (p: any) => p.level !== null && p.level !== undefined
  ).length;
  
  if (completedSkills === 0) return null;
  
  // Calculate percentage based on completed skills
  return (completedSkills / totalSkillsCount) * 100;
};

// Función auxiliar para extraer RUT del estudiante
const extractRutFromStudent = (student: Student): string => {
  // PRIORIDAD 1: Si el estudiante tiene originalId (contiene RUT completo con K)
  if ((student as any).originalId) {
    const originalId = String((student as any).originalId);
    // Verificar que sea un RUT válido con K
    if (/^[0-9]{7,8}[0-9kK]$/i.test(originalId)) {
      return originalId.replace(/\./g, '').replace(/-/g, '');
    }
  }
  
  // PRIORIDAD 2: Si el estudiante tiene metadata.rut
  if ((student as any).metadata?.rut) {
    return (student as any).metadata.rut.replace(/\./g, '').replace(/-/g, '');
  }
  
  // PRIORIDAD 3: Convertir ID a string para trabajar con él
  const studentIdStr = String(student.id);
  
  // Si el RUT está en el ID como string con formato válido
  if (/^[0-9]{7,8}[0-9kK]$/i.test(studentIdStr)) {
    return studentIdStr.replace(/\./g, '').replace(/-/g, '');
  }
  
  // PRIORIDAD 4: Intentar extraer RUT del nombre
  const nameMatch = student.name.match(/\b[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9kK]\b/);
  if (nameMatch) {
    return nameMatch[0].replace(/\./g, '').replace(/-/g, '');
  }
  
  // ÚLTIMO RECURSO: usar el ID convertido a string
  return studentIdStr;
};

// Componente Principal
const StudentSkillsTrackingSystem: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  
  // 🌓 Manejo de Tema (Modo Claro/Oscuro) - Default Claro
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    // Si no hay preferencia guardada, usamos claro (false)
    return savedTheme === 'dark';
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<(number | string)[]>([]);
  const [selectedDimension, setSelectedDimension] = useState<number | null>(null);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  
  // Estados críticos que deben estar definidos antes de los useEffect
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [role, setRole] = useState<'admin' | 'teacher' | '' >('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveProgressModal, setShowSaveProgressModal] = useState(false);
  const [allowedCourses, setAllowedCourses] = useState<string[]>([]);
  const [allowedDimensions, setAllowedDimensions] = useState<number[]>([]);
  const loadingRef = useRef(false);
  const loadGenerationRef = useRef(0);

  // HTTP-only coordination - no socket references needed
  const [lockedStudents, setLockedStudents] = useState<{[key: number]: {lockedBy: string, expires: Date}}>({});

  // Obtener el contexto HTTP-only DENTRO DEL COMPONENTE
  const socketContext: SocketContextType = useSocketContext();
  
  // Obtener contexto de tenant
  const { tenantKey } = useTenant();

  const ageGroups = ["3-4 Años", "4-5 Años", "5-6 Años", "6-7 Años", "7-8 Años"];

  const [courses, setCourses] = useState<string[]>([]);

  const [token, setToken] = useState<string | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // 🔄 Inicializar token manager para renovación automática
  useEffect(() => {
    console.log('🔄 Inicializando token manager...');
    
    // Configurar verificación proactiva de tokens cada 5 minutos
    const tokenCheckInterval = setInterval(() => {
      if (authenticated && token) {
        tokenManager.proactiveRefresh();
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(tokenCheckInterval);
  }, [authenticated, token]);

  // API_BASE_URL importado desde apiConfig.ts

  // Modificar la función de ordenamiento de cursos para usar la función centralizada
  const sortCourses = (courses: string[]): string[] => {
    return courses.sort((a, b) => {
      const orderA = getCourseOrderIndex(a);
      const orderB = getCourseOrderIndex(b);
      
      if (orderA === orderB) {
        // Si los cursos base son iguales, ordenar por la letra (A, B, C)
        return a.localeCompare(b);
      }
      
      return orderA - orderB;
    });
  };

  // Agregar esta función auxiliar para extraer el primer apellido
  const getFirstLastName = useCallback((fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // Si hay al menos 3 partes (nombre y dos apellidos), tomamos la penúltima
    if (nameParts.length >= 3) {
      return nameParts[nameParts.length - 2].toLowerCase();
    }
    // Si hay 2 partes, asumimos que es nombre y apellido
    if (nameParts.length === 2) {
      return nameParts[1].toLowerCase();
    }
    // Si solo hay una parte o está vacío, retornamos la cadena vacía
    return '';
  }, []);

  // Modificar loadBackupData para incluir el ordenamiento
  // 🧪 FORCE API LOADING - Testing function
  const forceLoadFromAPI = useCallback(async () => {
    try {
      console.log("🧪 FORCE LOADING from MongoDB API - NO JSON FALLBACK!");
      
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.error('❌ CRITICAL: No auth token available!');
        alert('❌ No auth token - cannot load from API');
        return;
      }

      console.log(`🔗 FORCING API call to /api/students with token: ${authToken.substring(0, 20)}...`);
      
      const response = await fetch('/api/students', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: AbortSignal.timeout(60000)
      });

      console.log(`📡 FORCED API Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const apiData = await response.json();
      console.log(`🎯 FORCED API Response:`, {
        success: apiData.success,
        count: apiData.count,
        tenant: apiData.tenant,
        firstFewStudents: apiData.students?.slice(0, 5)?.map(s => ({ id: s.id, name: s.name }))
      });
      
      if (!apiData.success) {
        throw new Error(`API Error: ${apiData.message}`);
      }

      const studentsData = apiData.students || [];
      console.log(`🎯 FORCED API - Loaded ${studentsData.length} students from MongoDB`);
      
      // Transform API data to match frontend Student interface
      const initialStudents: Student[] = studentsData.map((studentData: any) => {
        // Create a map for quick lookup of skill levels
        const skillLevelsMap = new Map();
        if (studentData.skillLevels && Array.isArray(studentData.skillLevels)) {
          studentData.skillLevels.forEach(skillLevel => {
            const key = `${skillLevel.dimensionId}-${skillLevel.skillId}`;
            skillLevelsMap.set(key, skillLevel.level);
          });
        }

        return {
          ...studentData,
          id: normalizeStudentId(studentData.id), // Normalize ID by removing dashes
          skillLevels: studentData.skillLevels || [],
          age: studentData.age || calculateAge(studentData.birthDate || ''),
          gender: studentData.gender || '',
          run: studentData.metadata?.rut || studentData.run || '',
          dimensions: dimensionsTemplate.map(dimension => ({
            ...dimension,
            skills: dimension.skills.map(skill => {
              // Map skillLevels to selectedLevelIndex
              const key = `${dimension.id}-${skill.id}`;
              const skillLevel = skillLevelsMap.get(key);
              
              // Convert level to selectedLevelIndex
              let selectedLevelIndex = null;
              if (skillLevel !== null && skillLevel !== undefined && skillLevel >= 0) {
                selectedLevelIndex = skillLevel;
              }
              
              return { 
                ...skill, 
                selectedLevelIndex: selectedLevelIndex
              };
            })
          }))
        };
      });

      const sortedInitialStudents = [...initialStudents].sort((a, b) => {
        const lastNameA = getFirstLastName(a.name);
        const lastNameB = getFirstLastName(b.name);
        return lastNameA.localeCompare(lastNameB);
      });

      setAllStudents(sortedInitialStudents);
      
      const uniqueCourses = Array.from(new Set(sortedInitialStudents.map((student: Student) => student.course)));
      const sortedCourses = sortCourses(uniqueCourses as string[]);
      setCourses(sortedCourses);
      
      if (sortedCourses.length > 0) {
        const defaultCourse = sortedCourses[0];
        setSelectedCourse(defaultCourse);
        setStudents(sortedInitialStudents.filter((student: Student) => student.course === defaultCourse));
      }
      
      console.log(`✅ FORCED API SUCCESS: ${sortedInitialStudents.length} students loaded from MongoDB API!`);
      console.log(`🎯 Available student IDs (first 10):`, sortedInitialStudents.slice(0, 10).map(s => s.id));
      
      alert(`✅ SUCCESS! Loaded ${sortedInitialStudents.length} students from MongoDB API`);
      
    } catch (error) {
      console.error("❌ FORCED API ERROR:", error);
      alert(`❌ API Error: ${error.message}`);
    }
  }, [getFirstLastName]);

  // 🎯 TEST SPECIFIC STUDENT - Test with a student that ACTUALLY exists
  const testSpecificStudent = useCallback(async () => {
    try {
      // Use the first ID from the current student list (after migration)
      const firstAvailableId = allStudents.length > 0 ? allStudents[0].id : null;
      let testStudentId = firstAvailableId; // Use first student from current migrated data
      console.log(`🎯 TESTING SPECIFIC STUDENT: ${testStudentId} (CONFIRMED WORKING ID FROM LOGS)`);
      
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        alert('❌ No auth token available for testing');
        return;
      }

      // First, verify student exists via debug endpoint
      console.log(`🧪 Step 1: Verifying student ${testStudentId} exists via debug endpoint...`);
      const debugResponse = await fetch(`/api/debug/student/${testStudentId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const debugResult = await debugResponse.json();
      console.log("🧪 Debug result:", debugResult);
      
      if (!debugResult.found) {
        alert(`❌ Student ${testStudentId} not found even in debug endpoint! Trying fallback IDs...`);
        // Try with confirmed existing IDs from logs
        const altStudentIds = [268457771, 270055699, 269577231]; // String IDs from logs
        console.log(`🔄 Trying alternative student IDs: ${altStudentIds.join(', ')}`);
        
        for (const altId of altStudentIds) {
          const altDebugResponse = await fetch(`/api/debug/student/${altId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const altDebugResult = await altDebugResponse.json();
          console.log(`🧪 Testing altId ${altId}: found=${altDebugResult.found}`);
          
          if (altDebugResult.found) {
            console.log(`✅ Alternative student ${altId} EXISTS! Using it for test...`);
            testStudentId = altId;
            break; // Use this working ID
          }
        }
        
        // If no alternative ID worked, alert and return
        if (testStudentId === 26329841) { // Original ID still hasn't changed
          alert(`❌ No students found with any tested ID! Check MongoDB data.`);
          return;
        }
      }
      
      console.log(`✅ Student ${testStudentId} EXISTS! Now testing save...`);
      
      // Now test save operation
      console.log("🧪 Step 2: Testing save operation...");
      const savePayload = {
        changes: [{
          id: testStudentId,
          skillLevels: [
            { dimensionId: 1, skillId: 1, level: 3 },
            { dimensionId: 1, skillId: 2, level: 3 }
          ],
          qualitativeDescription: `TEST SAVE - Student ID ${testStudentId}`
        }]
      };
      
      console.log("🎯 Save payload:", savePayload);
      
      const saveResponse = await fetch(`${API_BASE_URL}/api/save-progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(savePayload)
      });
      
      const saveResult = await saveResponse.json();
      console.log("🎯 Save result:", saveResult);
      
      if (saveResult.success) {
        alert(`✅ SUCCESS! Student ${testStudentId} saved successfully!`);
        console.log(`🎉 GUARDADO EXITOSO! El problema está resuelto.`);
      } else {
        alert(`❌ FAILED: ${saveResult.message}`);
        console.log(`❌ Logs del error:`, saveResult);
      }
      
    } catch (error) {
      console.error("❌ Test error:", error);
      alert(`❌ Test error: ${error.message}`);
    }
  }, [API_BASE_URL]);

  // 🧪 DEBUGGING: Compare what /api/students vs /api/load-progress return
  const debugEndpointComparison = useCallback(async () => {
    try {
      console.log("🧪 DEBUGGING: Comparing /api/students vs /api/load-progress");
      
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        alert('❌ No auth token for debugging');
        return;
      }

      // Test /api/students
      console.log("🔍 Step 1: Testing /api/students endpoint...");
      const studentsResponse = await fetch('/api/students', {
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(60000)
      });
      const studentsData = await studentsResponse.json();
      
      console.log("🎯 /api/students result:", {
        success: studentsData.success,
        count: studentsData.count,
        first5IDs: studentsData.students?.slice(0, 5)?.map(s => s.id)
      });

      // Test /api/load-progress
      console.log("🔍 Step 2: Testing /api/load-progress endpoint...");
      const loadResponse = await fetch(`${API_BASE_URL}/api/load-progress`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const loadData = await loadResponse.json();
      
      console.log("🎯 /api/load-progress result:", {
        success: loadData.success,
        count: loadData.students?.length,
        first5IDs: loadData.students?.slice(0, 5)?.map(s => s.id)
      });

      // Compare them
      const studentsIDs = studentsData.students?.map(s => String(s.id)) || [];
      const loadIDs = loadData.students?.map(s => String(s.id)) || [];
      
      console.log("🔍 COMPARISON:");
      console.log(`   /api/students IDs: [${studentsIDs.slice(0, 5).join(', ')}...]`);
      console.log(`   /api/load-progress IDs: [${loadIDs.slice(0, 5).join(', ')}...]`);
      
      const firstStudentID = studentsIDs[0] || loadIDs[0];
      if (firstStudentID) {
        console.log(`🧪 Step 3: Testing if first ID ${firstStudentID} exists via debug endpoint...`);
        const debugResponse = await fetch(`/api/debug/student/${firstStudentID}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        const debugResult = await debugResponse.json();
        console.log(`🎯 Debug result for ${firstStudentID}:`, debugResult);
        
        alert(`🧪 Debug Results:
/api/students: ${studentsData.count || 0} students
/api/load-progress: ${loadData.students?.length || 0} students
First ID ${firstStudentID}: ${debugResult.found ? 'EXISTS' : 'NOT FOUND'} in MongoDB

Check console for detailed logs.`);
      } else {
        alert('❌ No student IDs found in any endpoint!');
      }
      
    } catch (error) {
      console.error("❌ Debug comparison error:", error);
      alert(`❌ Debug error: ${error.message}`);
    }
  }, [API_BASE_URL]);

  // 🔄 LOAD BACKUP DATA: First declare the function that will be used as dependency
  const loadBackupData = useCallback(async () => {
    try {
      console.log("🚀 Loading students from backend API instead of JSON file...");
      
      // Get auth token from localStorage
      const authToken = localStorage.getItem('token');
      console.log(`🔍 AUTH TOKEN CHECK: ${authToken ? 'FOUND' : 'NOT_FOUND'}`);
      if (!authToken) {
        console.error('❌ CRITICAL: No auth token - cannot load from API');
        const allowJsonFallback =
          process.env.NODE_ENV === 'development' &&
          process.env.REACT_APP_ALLOW_JSON_FALLBACK === 'true';

        if (!allowJsonFallback) {
          throw new Error('No hay token de autenticación');
        }

        console.warn("⚠️ Usando datos de respaldo — NO son datos en vivo");
        const response = await fetch('/text/Estudiantes CPV Informe.json');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const studentsData = await response.json();
      const initialStudents: Student[] = studentsData.map((studentData: any) => {
        // Create a map for quick lookup of skill levels
        const skillLevelsMap = new Map();
        if (studentData.skillLevels && Array.isArray(studentData.skillLevels)) {
          studentData.skillLevels.forEach(skillLevel => {
            const key = `${skillLevel.dimensionId}-${skillLevel.skillId}`;
            skillLevelsMap.set(key, skillLevel.level);
          });
        }

        return {
          ...studentData,
          id: normalizeStudentId(studentData.id), // Normalize ID by removing dashes
          skillLevels: studentData.skillLevels || [], // Ensure skillLevels is initialized
          age: studentData.age || calculateAge(studentData.birthDate || ''),
          dimensions: dimensionsTemplate.map(dimension => ({
            ...dimension,
            skills: dimension.skills.map(skill => {
              // Map skillLevels to selectedLevelIndex
              const key = `${dimension.id}-${skill.id}`;
              const skillLevel = skillLevelsMap.get(key);
              
              // Convert level to selectedLevelIndex
              let selectedLevelIndex = null;
              if (skillLevel !== null && skillLevel !== undefined && skillLevel >= 0) {
                selectedLevelIndex = skillLevel;
              }
              
              return { 
                ...skill, 
                selectedLevelIndex: selectedLevelIndex
              };
            })
          }))
        };
      });
        
        const sortedInitialStudents = [...initialStudents].sort((a, b) => {
          const lastNameA = getFirstLastName(a.name);
          const lastNameB = getFirstLastName(b.name);
          return lastNameA.localeCompare(lastNameB);
        });
        
        setAllStudents(sortedInitialStudents);
        
        const uniqueCourses = Array.from(new Set(sortedInitialStudents.map((student: Student) => student.course)));
        const sortedCourses = sortCourses(uniqueCourses as string[]);
        setCourses(sortedCourses);
        
        if (sortedCourses.length > 0) {
          const defaultCourse = sortedCourses[0];
          setSelectedCourse(defaultCourse);
          setStudents(sortedInitialStudents.filter((student: Student) => student.course === defaultCourse));
        }
        return;
      }

      // 🎯 NEW: Load from backend API with authentication
      console.log(`🔗 Making API call to /api/students with token: ${authToken.substring(0, 20)}...`);
      
      const response = await fetch(`${API_BASE_URL}/api/students`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: AbortSignal.timeout(60000)
      });

      console.log(`📡 API Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('🔐 Token de autenticación inválido o expirado');
          throw new Error('Token de autenticación inválido');
        }
        console.error(`❌ API call failed: ${response.status} ${response.statusText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const apiData = await response.json();
      console.log(`🎯 API Response:`, {
        success: apiData.success,
        count: apiData.count,
        tenant: apiData.tenant,
        timestamp: apiData.timestamp,
        firstFewStudents: apiData.students?.slice(0, 3)?.map(s => ({ id: s.id, name: s.name }))
      });
      
      if (!apiData.success) {
        throw new Error(`API Error: ${apiData.message}`);
      }

      const studentsData = apiData.students || [];
      console.log(`📊 Loaded ${studentsData.length} students from backend API`);
      
      // Check for the problematic student ID in frontend data
      const targetStudent = studentsData.find(s => String(s.id) === "263298411" || Number(s.id) === 263298411);
      if (targetStudent) {
        console.log(`🎯 TARGET STUDENT 263298411 FOUND in frontend data: Name="${targetStudent.name}"`);
      } else {
        console.log(`❌ TARGET STUDENT 263298411 NOT FOUND in frontend data`);
        console.log(`🔍 Available student IDs (first 10):`, studentsData.slice(0, 10).map(s => s.id));
      }
      
      // Transform API data to match frontend Student interface
      const initialStudents: Student[] = studentsData.map((studentData: any) => {
        // Create a map for quick lookup of skill levels
        const skillLevelsMap = new Map();
        if (studentData.skillLevels && Array.isArray(studentData.skillLevels)) {
          studentData.skillLevels.forEach(skillLevel => {
            const key = `${skillLevel.dimensionId}-${skillLevel.skillId}`;
            skillLevelsMap.set(key, skillLevel.level);
          });
        }

        return {
          ...studentData,
          id: normalizeStudentId(studentData.id), // Normalize ID by removing dashes
          skillLevels: studentData.skillLevels || [], // Ensure skillLevels is initialized
          // Add any missing fields with defaults
          age: studentData.age || calculateAge(studentData.birthDate || ''),
          gender: studentData.gender || '',
          run: studentData.metadata?.rut || studentData.run || '',
          dimensions: dimensionsTemplate.map(dimension => ({
            ...dimension,
            skills: dimension.skills.map(skill => {
              // Map skillLevels to selectedLevelIndex
              const key = `${dimension.id}-${skill.id}`;
              const skillLevel = skillLevelsMap.get(key);
              
              // Convert level to selectedLevelIndex
              let selectedLevelIndex = null;
              if (skillLevel !== null && skillLevel !== undefined && skillLevel >= 0) {
                selectedLevelIndex = skillLevel;
              }
              
              return { 
                ...skill, 
                selectedLevelIndex: selectedLevelIndex
              };
            })
          }))
        };
      });

      // Ordenar los estudiantes por primer apellido
      const sortedInitialStudents = [...initialStudents].sort((a, b) => {
        const lastNameA = getFirstLastName(a.name);
        const lastNameB = getFirstLastName(b.name);
        return lastNameA.localeCompare(lastNameB);
      });

      setAllStudents(sortedInitialStudents);
      
      const uniqueCourses = Array.from(new Set(sortedInitialStudents.map((student: Student) => student.course)));
      const sortedCourses = sortCourses(uniqueCourses as string[]);
      setCourses(sortedCourses);
      
      if (sortedCourses.length > 0) {
        const defaultCourse = sortedCourses[0];
        setSelectedCourse(defaultCourse);
        setStudents(sortedInitialStudents.filter((student: Student) => student.course === defaultCourse));
      }
      
              console.log(`✅ Successfully loaded ${sortedInitialStudents.length} students from backend API`);
      
    } catch (error) {
      console.error("❌ Error al cargar estudiantes desde backend API:", error);

      const allowJsonFallback =
        process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_ALLOW_JSON_FALLBACK === 'true';

      if (!allowJsonFallback) {
        throw error;
      }

      console.warn("⚠️ Usando datos de respaldo — NO son datos en vivo");
      console.log("🔄 Fallback: Intentando cargar desde archivo JSON de respaldo...");
      
      try {
        const response = await fetch('/text/Estudiantes CPV Informe.json');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const studentsData = await response.json();
        const initialStudents: Student[] = studentsData.map((studentData: any) => ({
          ...studentData,
          id: Number(studentData.id), // Ensure ID is a number
          skillLevels: studentData.skillLevels || [], // Ensure skillLevels is initialized
          dimensions: dimensionsTemplate.map(dimension => ({
            ...dimension,
            skills: dimension.skills.map(skill => ({ 
              ...skill, 
              selectedLevelIndex: null 
            }))
          }))
        }));

        // Ordenar los estudiantes por primer apellido
        const sortedInitialStudents = [...initialStudents].sort((a, b) => {
          const lastNameA = getFirstLastName(a.name);
          const lastNameB = getFirstLastName(b.name);
          return lastNameA.localeCompare(lastNameB);
        });

        setAllStudents(sortedInitialStudents);
        
        const uniqueCourses = Array.from(new Set(sortedInitialStudents.map((student: Student) => student.course)));
        const sortedCourses = sortCourses(uniqueCourses as string[]);
        setCourses(sortedCourses);
        
        if (sortedCourses.length > 0) {
          const defaultCourse = sortedCourses[0];
          setSelectedCourse(defaultCourse);
          setStudents(sortedInitialStudents.filter((student: Student) => student.course === defaultCourse));
        }
        
        console.log(`📄 Loaded ${sortedInitialStudents.length} students from JSON fallback`);
        console.warn(`⚠️ USING JSON FALLBACK: Save operations will fail because backend uses MongoDB!`);
      } catch (fallbackError) {
        console.error("❌ Error al cargar datos de respaldo desde JSON:", fallbackError);
        console.error("💥 TOTAL FAILURE: Neither API nor JSON fallback worked!");
      }
    }
  }, [API_BASE_URL, getFirstLastName]);

  const isRetryableLoadError = (error: any): boolean => {
    if (error?.code === 'ECONNABORTED') return true;
    const status = error?.response?.status;
    if (status === 503) return true;
    if (status === 500 && error?.response?.data?.error === 'LOAD_PROGRESS_TIMEOUT') return true;
    return false;
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const applyLoadedStudents = useCallback((
    loadedStudents: Student[],
    generation: number
  ): boolean => {
    if (generation !== loadGenerationRef.current) {
      console.log('Ignorando respuesta de carga obsoleta');
      return false;
    }

    const sortedLoadedStudents = [...loadedStudents].sort((a, b) => {
      const lastNameA = getFirstLastName(a.name);
      const lastNameB = getFirstLastName(b.name);
      return lastNameA.localeCompare(lastNameB);
    });

    console.log(`Procesados ${sortedLoadedStudents.length} estudiantes`);
    setAllStudents(sortedLoadedStudents);

    const uniqueCourses = Array.from(new Set(sortedLoadedStudents.map(student => student.course)));
    const sortedCourses = sortCourses(uniqueCourses);
    setCourses(sortedCourses);

    if (sortedCourses.length > 0) {
      const defaultCourse = sortedCourses[0];
      setSelectedCourse(defaultCourse);
      const filteredStudents = sortedLoadedStudents.filter(student => student.course === defaultCourse);
      setStudents(filteredStudents);
    }

    setUnsavedChanges(false);
    setLastSyncTimestamp(Date.now());
    setUpdatesAvailable(false);
    return true;
  }, [getFirstLastName]);

  const loadProgress = useCallback(async (
    authToken: string,
    expectedGeneration?: number
  ): Promise<boolean> => {
    const generation = expectedGeneration ?? ++loadGenerationRef.current;

    if (loadingRef.current) {
      console.log('Carga de datos ya en progreso, esperando...');
    }

    loadingRef.current = true;
    setIsLoading(true);

    const maxAttempts = 3;
    const retryDelays = [2000, 4000];

    try {
      console.log("Iniciando carga de datos...");

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (generation !== loadGenerationRef.current) {
          console.log('Carga cancelada: sesión más reciente detectada');
          return false;
        }

        try {
          const response = await axios.get<{ success: boolean; students: Student[] }>(
            `${API_BASE_URL}/api/load-progress`,
            {
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 60000
            }
          );

          if (response.data?.success && Array.isArray(response.data?.students)) {
            const loadedStudents = response.data.students.map(student => {
              const skillLevelsMap = new Map();
              if (student.skillLevels && Array.isArray(student.skillLevels)) {
                student.skillLevels.forEach(skillLevel => {
                  const key = `${skillLevel.dimensionId}-${skillLevel.skillId}`;
                  skillLevelsMap.set(key, skillLevel.level);
                });
              }

              return {
                ...student,
                id: normalizeStudentId(student.id),
                skillLevels: student.skillLevels || [],
                age: student.age || calculateAge(student.birthDate || ''),
                dimensions: dimensionsTemplate.map(dimension => ({
                  ...dimension,
                  skills: dimension.skills.map(skill => {
                    const key = `${dimension.id}-${skill.id}`;
                    const skillLevel = skillLevelsMap.get(key);
                    let selectedLevelIndex = null;
                    if (skillLevel !== null && skillLevel !== undefined && skillLevel >= 0) {
                      selectedLevelIndex = skillLevel;
                    }
                    return {
                      ...skill,
                      selectedLevelIndex
                    };
                  })
                }))
              };
            });

            if (applyLoadedStudents(loadedStudents, generation)) {
              console.log("Carga de datos completada exitosamente");
              return true;
            }
            return false;
          }

          throw new Error("Formato de datos inválido");
        } catch (error: any) {
          const canRetry = attempt < maxAttempts && isRetryableLoadError(error);
          console.error(`Error al cargar datos (intento ${attempt}/${maxAttempts}):`, error?.message || error);

          if (canRetry) {
            const delay = retryDelays[attempt - 1] ?? 4000;
            console.log(`Reintentando carga en ${delay / 1000}s...`);
            await sleep(delay);
            continue;
          }

          throw error;
        }
      }

      return false;
    } catch (error: any) {
      console.error("No se pudieron cargar los datos de estudiantes:", error);
      return false;
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, [API_BASE_URL, applyLoadedStudents]);
  const executeCleanMigration = useCallback(async () => {
    try {
      console.log("🚨 EXECUTING CLEAN MIGRATION: Eliminating all students and migrating fresh from JSON");
      
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        alert('❌ No auth token available for migration');
        return;
      }

      // Confirm dangerous operation
      const confirmed = window.confirm(
        `🚨 MIGRACIÓN LIMPIA\n\n` +
        `Esta operación eliminará TODOS los estudiantes existentes (${allStudents.length}) ` +
        `y los migrará nuevamente desde el archivo JSON original.\n\n` +
        `¿Estás seguro de que deseas continuar?\n\n` +
        `Esta acción NO se puede deshacer.`
      );
      
      if (!confirmed) {
        console.log("🚫 Clean migration cancelled by user");
        return;
      }

      // Show progress alert
      alert('🔄 Iniciando migración limpia... Esta operación puede tomar varios minutos. Verifica los logs de la consola.');

      console.log("🚨 Starting clean migration...");
      const response = await fetch('/api/critical-fix/clean-migration', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      console.log("🚨 Clean migration result:", result);
      
      if (result.success) {
        alert(
          `✅ MIGRACIÓN LIMPIA COMPLETADA\n\n` +
          `• Estudiantes eliminados: ${result.stats.deletedCount}\n` +
          `• Estudiantes migrados: ${result.stats.migratedSuccessfully}\n` +
          `• Total final: ${result.stats.finalTotalCount}\n` +
          `• Con tenantKey: ${result.stats.finalTenantCount}\n` +
          `• Errores: ${result.stats.errors}\n\n` +
          `🔄 Refrescando datos...`
        );
        
        // Reload data from API
        console.log("🔄 Reloading data after clean migration...");
        await loadBackupData();
        
      } else {
        alert(`❌ Error en migración limpia: ${result.message}`);
        console.error("❌ Clean migration failed:", result);
      }
      
    } catch (error) {
      console.error("❌ Clean migration error:", error);
      alert(`❌ Error ejecutando migración limpia: ${error.message}`);
    }
  }, [allStudents.length, loadBackupData]);

  // Modificar loadProgress para actualizar el timestamp después de cargar datos
  // Función para recargar estudiantes desde el servidor (para actualizar datos en exportación)
  const refreshStudentsData = useCallback(async () => {
    try {
      console.log("🔄 Refrescando datos de estudiantes para exportación...");

      const response = await axios.get<{success: boolean; students: Student[]}>(
        `${API_BASE_URL}/api/load-progress`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (response.data?.success && Array.isArray(response.data?.students)) {
        const refreshedStudents = response.data.students.map(student => {
          const skillLevelsMap = new Map();
          if (student.skillLevels && Array.isArray(student.skillLevels)) {
            student.skillLevels.forEach(skillLevel => {
              skillLevelsMap.set(`${skillLevel.dimensionId}-${skillLevel.skillId}`, skillLevel);
            });
          }

          return {
            ...student,
            id: normalizeStudentId(student.id),
            skillLevels: student.skillLevels || [],
            qualitativeDescription: student.qualitativeDescription || '',
            birthDate: student.birthDate || '',
            course: student.course || '',
            name: student.name || ''
          };
        });

        // Actualizar el estado con los datos refrescados
        setStudents(refreshedStudents);
        setAllStudents(refreshedStudents);

        console.log(`✅ Datos refrescados: ${refreshedStudents.length} estudiantes actualizados`);
      }
    } catch (error) {
      console.error('❌ Error al refrescar datos de estudiantes:', error);
    }
  }, []);

  // Modificar el useEffect inicial para manejar tokens del sistema y de usuarios
  useEffect(() => {
    // Solo ejecutar una vez al montar el componente
    if (initialLoadDone) return;
    
    const savedToken = localStorage.getItem('token');
    const storedUsername = localStorage.getItem('username');
    
    if (savedToken && storedUsername) {
      // Verificar si es el admin del sistema
      if (storedUsername === SYSTEM_ADMIN_EMAIL) {
        // Para el admin del sistema, restaurar directamente desde localStorage
        const storedRole = localStorage.getItem('userRole');
        const storedCourses = localStorage.getItem('allowedCourses');
        const storedDimensions = localStorage.getItem('allowedDimensions');
        
        setToken(savedToken);
        setUsername(storedUsername);
        setRole(storedRole as 'admin' | 'teacher' || 'admin');
        
        if (storedCourses) {
          try {
            setAllowedCourses(JSON.parse(storedCourses));
          } catch (e) {
            setAllowedCourses(['Todos']);
          }
        }
        if (storedDimensions) {
          try {
            const parsed = JSON.parse(storedDimensions);
            const dimensionsAsNumbers = Array.isArray(parsed)
              ? parsed.map(d => typeof d === 'string' ? parseInt(d, 10) : Number(d)).filter(d => !isNaN(d))
              : [];
            setAllowedDimensions(dimensionsAsNumbers);
          } catch (e) {
            setAllowedDimensions([]);
          }
        } else {
          // Si no hay dimensiones guardadas, establecer array vacío para mostrar todas
          setAllowedDimensions([]);
        }
        
        setAuthenticated(true);
        setActiveSection('dashboard'); // Ir directamente a la vista de resumen/dashboard
        
        console.log('Admin del sistema restaurado desde localStorage');
        setIsLoading(false);
        setInitialLoadDone(true);
        return;
      }
      
      // Para otros usuarios, verificar el token con el servidor
      const verifyToken = async () => {
        const generation = ++loadGenerationRef.current;

        try {
          // Check if token should be cleared due to expiration before making request
          const { tokenManager } = await import('./utils/tokenManager');
          if (tokenManager.shouldClearSession()) {
            console.log('🔄 Token detectado como expirado, limpiando sesión...');
            tokenManager.clearExpiredSession();
            return; // clearExpiredSession already handles the reload
          }

          const response = await axios.get(`${API_BASE_URL}/api/verify-token`, {
            headers: {
              'Authorization': `Bearer ${savedToken}`
            }
          });
          
          if (response.data.valid && response.data.user) {
            // Establecer toda la información del usuario desde el servidor (fuente de verdad)
            setToken(savedToken);
            setUsername(response.data.user.username);
            setRole(response.data.user.role);
            
            // Usar datos del servidor si están disponibles, sino caer a localStorage
            const serverCourses = response.data.user.allowedCourses;
            const serverDimensions = response.data.user.allowedDimensions;
            
            if (serverCourses && serverCourses.length > 0) {
              setAllowedCourses(serverCourses);
              localStorage.setItem('allowedCourses', JSON.stringify(serverCourses));
            } else {
              const storedCourses = localStorage.getItem('allowedCourses');
              if (storedCourses) {
                try {
                  setAllowedCourses(JSON.parse(storedCourses));
                } catch (e) {
                  setAllowedCourses([storedCourses]);
                }
              }
            }
            
            // Dimensiones: usar servidor como fuente de verdad
            if (serverDimensions !== undefined) {
              const dimensionsAsNumbers = Array.isArray(serverDimensions) 
                ? serverDimensions.map(d => typeof d === 'string' ? parseInt(d, 10) : Number(d)).filter(d => !isNaN(d))
                : [];
              setAllowedDimensions(dimensionsAsNumbers);
              localStorage.setItem('allowedDimensions', JSON.stringify(dimensionsAsNumbers));
              console.log("✅ Dimensiones cargadas desde servidor:", {
                serverDimensions: serverDimensions,
                dimensionsAsNumbers: dimensionsAsNumbers,
                shouldShowAllDimensions: dimensionsAsNumbers.length === 0
              });
            } else {
              const storedDimensions = localStorage.getItem('allowedDimensions');
              if (storedDimensions) {
                try {
                  const parsed = JSON.parse(storedDimensions);
                  const dimensionsAsNumbers = Array.isArray(parsed)
                    ? parsed.map(d => typeof d === 'string' ? parseInt(d, 10) : Number(d)).filter(d => !isNaN(d))
                    : [];
                  setAllowedDimensions(dimensionsAsNumbers);
                } catch (e) {
                  setAllowedDimensions([]);
                }
              } else {
                // Si no hay dimensiones guardadas, establecer array vacío para mostrar todas
                setAllowedDimensions([]);
              }
              console.log("✅ Dimensiones cargadas desde localStorage:", {
                storedDimensions: storedDimensions,
                parsedDimensions: storedDimensions ? JSON.parse(storedDimensions) : [],
                shouldShowAllDimensions: !storedDimensions || JSON.parse(storedDimensions || '[]').length === 0
              });
            }
            
            setAuthenticated(true);
            
            console.log('Token válido, usuario restaurado:', {
              username: response.data.user.username,
              role: response.data.user.role
            });
            
            // Cargar los datos después de establecer la información del usuario
            const loaded = await loadProgress(savedToken, generation);
            if (!loaded) {
              console.error('No se pudieron cargar los datos al restaurar sesión');
              setAuthenticated(false);
            }
          } else {
            // Si el token no es válido, usar token manager para limpiar sesión
            const { tokenManager } = await import('./utils/tokenManager');
            tokenManager.clearExpiredSession();
          }
        } catch (error) {
          console.error('Error verificando token:', error);
          
          // Check if it's a token expiration error
          if (error.response?.status === 403 || 
              (error.response?.data?.message?.includes('expired') ||
               error.response?.data?.message?.includes('inválido') ||
               error.response?.data?.message?.includes('Token'))) {
            // Use token manager to clear session properly
            const { tokenManager } = await import('./utils/tokenManager');
            tokenManager.clearExpiredSession();
          } else {
            // For other errors, just clear localStorage and continue
            localStorage.removeItem('token');
            localStorage.removeItem('username');
            localStorage.removeItem('userRole');
            localStorage.removeItem('allowedCourses');
            localStorage.removeItem('allowedDimensions');
            setAuthenticated(false);
            setIsLoading(false);
            setInitialLoadDone(true);
          }
        } finally {
          // Only set these if we haven't redirected
          const token = localStorage.getItem('token');
          if (token) {
            setIsLoading(false);
            setInitialLoadDone(true);
          }
        }
      };

      verifyToken();
    } else {
      setIsLoading(false);
      setInitialLoadDone(true);
    }
  }, [API_BASE_URL, initialLoadDone, loadProgress]); // Sin dependencias para que solo se ejecute una vez

  // Primero, agregar los estados necesarios
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  // "Cambiar contraseña" del menú de usuario
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetCurrentPassword, setResetCurrentPassword] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);
  const [sessionTimeout, setSessionTimeout] = useState<NodeJS.Timeout | null>(null);
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  
  // Estados movidos al principio del componente para evitar loops infinitos

  // Agregar una función para actualizar la actividad del usuario
  const updateUserActivity = useCallback(() => {
    setLastActivity(new Date());
  }, []);

  // Definir handleLogout antes de usarlo en useEffect
  const handleLogout = useCallback(() => {
    // Limpiar estados locales
    setAuthenticated(false);
    setUsername('');
    setPassword('');
    setToken(null);
    setRole('');
    setAllStudents([]);
    setStudents([]);
    setSelectedStudentIds([]);
    setSelectedDimension(null);
    setActiveSection('dashboard');
    
    // Importante: Eliminar datos de sesión
    localStorage.removeItem('token');
    localStorage.removeItem('lastLogin');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    localStorage.removeItem('allowedCourses');
    localStorage.removeItem('allowedDimensions');
    localStorage.removeItem('organizationData');
    localStorage.removeItem('tenantKey');
    localStorage.removeItem('tenantTheme');
    localStorage.removeItem('userData');
    sessionStorage.clear();
    
    // Restaurar título por defecto
    document.title = 'Sistema de Evaluación Integral';
    
    console.log("Logout completado");
  }, []);

  // Configurar session timeout
  useEffect(() => {
    if (authenticated) {
      // Configurar un tiempo máximo de inactividad (30 minutos)
      const SESSION_TIMEOUT = 30 * 60 * 1000;
      let currentSessionTimeout: NodeJS.Timeout | null = null;
      
      const sessionTimer = setInterval(() => {
        const now = new Date();
        // Usar una referencia actualizada de lastActivity
        const lastActivityTime = new Date(localStorage.getItem('lastActivity') || Date.now());
        if (now.getTime() - lastActivityTime.getTime() > SESSION_TIMEOUT) {
          // El usuario ha estado inactivo por demasiado tiempo
          handleLogout();
          alert("Su sesión ha expirado por inactividad. Por favor inicie sesión nuevamente.");
        }
      }, 60000); // Verificar cada minuto
      
      currentSessionTimeout = sessionTimer;
      
      // Agregar event listeners para registrar actividad del usuario
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      const activityHandler = () => {
        const now = new Date();
        setLastActivity(now);
        localStorage.setItem('lastActivity', now.toISOString());
      };
      
      events.forEach(event => window.addEventListener(event, activityHandler));
      
      return () => {
        if (currentSessionTimeout) {
          clearInterval(currentSessionTimeout);
        }
        events.forEach(event => window.removeEventListener(event, activityHandler));
      };
    }
  }, [authenticated, handleLogout]);

  // Obtener un CSRF token cuando la app inicia
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        // En un entorno real, esto sería una llamada a la API
        // para obtener un token CSRF desde el servidor
        const mockCsrfToken = Array(32).fill(0)
          .map(() => Math.random().toString(36).charAt(2))
          .join('');
        setCsrfToken(mockCsrfToken);
      } catch (error) {
        console.error("Error obteniendo CSRF token:", error);
      }
    };
    
    fetchCsrfToken();
  }, []);

  // Función para evaluar la fortaleza de la contraseña
  const evaluatePasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    if (!password || password.length < 8) return 'weak';
    
    let score = 0;
    // Verificar longitud
    if (password.length >= 12) score += 2;
    else if (password.length >= 8) score += 1;
    
    // Verificar complexity
    if (/[A-Z]/.test(password)) score += 1; // Mayúsculas
    if (/[a-z]/.test(password)) score += 1; // Minúsculas
    if (/[0-9]/.test(password)) score += 1; // Números
    if (/[^A-Za-z0-9]/.test(password)) score += 1; // Caracteres especiales
    
    if (score >= 5) return 'strong';
    if (score >= 3) return 'medium';
    return 'weak';
  };

  // Modificar handleLogin para usar UserService
  const handleLogin = async (username: string, password: string) => {
    try {
      setError("");
      setIsLoading(true);

      // Invalidar cargas en curso y limpiar datos de sesión anterior
      loadGenerationRef.current += 1;
      setAllStudents([]);
      setStudents([]);
      setCourses([]);
      setSelectedCourse('');
      setSelectedStudentIds([]);
      
      // Verificar si la cuenta está bloqueada
      if (lockoutUntil && new Date() < lockoutUntil) {
        const minutesLeft = Math.ceil((lockoutUntil.getTime() - new Date().getTime()) / 60000);
        setError(`Cuenta temporalmente bloqueada. Intente nuevamente en ${minutesLeft} minutos.`);
        setIsLoading(false);
        return;
      }
      
      // Validar datos de entrada
      if (!username || !password) {
        setError("Por favor complete todos los campos");
        setIsLoading(false);
        return;
      }
      
      console.log("Intentando iniciar sesión con:", { username });
      
      // Login especial para admin del sistema - usar endpoint real del backend
      if (username === SYSTEM_ADMIN_EMAIL) {
        console.log("🔐 Intentando login de admin del sistema...");
        
        try {
          const adminResponse = await fetch(`${API_BASE_URL}/api/system-admin/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          });
          
          const adminResult = await adminResponse.json();
          
          if (adminResponse.ok && adminResult.success && adminResult.token) {
            // Resetear contador de intentos
            setLoginAttempts(0);
            setLockoutUntil(null);
            
            console.log("✅ Login de admin del sistema exitoso");
            
            // Almacenar token JWT real del servidor
            setToken(adminResult.token);
            localStorage.setItem('token', adminResult.token);
            localStorage.setItem('lastLogin', new Date().toISOString());
            localStorage.setItem('username', username);
            localStorage.setItem('userRole', 'admin');
            localStorage.setItem('tenantKey', 'system');
            localStorage.setItem('allowedCourses', JSON.stringify(['Todos']));
            localStorage.setItem('allowedDimensions', JSON.stringify([]));
            
            // Guardar datos del usuario
            localStorage.setItem('userData', JSON.stringify({
              username,
              role: 'admin',
              tenantKey: 'system',
              isSystemAdmin: true
            }));
            
            setUsername(username);
            setRole('admin');
            setAllowedCourses(['Todos']);
            setAllowedDimensions([]);
            setAuthenticated(true);
            
            // Actualizar título del documento
            document.title = 'Administrador del Sistema - Sistema de Evaluación Integral';
            
            // Actualizar timestamp de actividad
            updateUserActivity();
            
            console.log("Login exitoso, navegando al dashboard...");
            
            // Navegar automáticamente al panel administrativo para admin del sistema
            setActiveSection('dashboard');
            console.log('Admin login exitoso, redirigiendo a panel administrativo');
            
            return;
          } else {
            // Si falla el login de admin, mostrar error
            setError(adminResult.message || "Credenciales de administrador inválidas");
            setIsLoading(false);
            return;
          }
        } catch (adminError) {
          console.error("Error en login de admin:", adminError);
          setError("Error al conectar con el servidor. Intente nuevamente.");
          setIsLoading(false);
          return;
        }
      }
      
      // Usar el endpoint multi-tenant de autenticación
      console.log("🔐 Iniciando login multi-tenant...");
      
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password
        }),
      });

      const authResult = await response.json();
      console.log("Respuesta de login multi-tenant:", authResult);
      
      // Verificar si el usuario está bloqueado
      if (authResult.blocked) {
        console.log("🚫 Usuario bloqueado:", authResult.reason);
        
        // Mostrar mensaje de bloqueo específico
        let blockMessage = authResult.message || 'Su acceso ha sido temporalmente suspendido.';
        
        if (authResult.blockType === 'maintenance') {
          blockMessage = `🔧 ${blockMessage}`;
        } else if (authResult.blockType === 'global') {
          blockMessage = `⚠️ ${blockMessage}`;
        } else {
          blockMessage = `🔒 ${blockMessage}`;
        }
        
        setError(blockMessage);
        setIsLoading(false);
        return;
      }
      
      if (response.ok && authResult.success && authResult.token) {
        // Resetear contador de intentos
        setLoginAttempts(0);
        setLockoutUntil(null);
        
        console.log("✅ Login exitoso, token JWT recibido");
        
        // Almacenar token JWT real del servidor
        setToken(authResult.token);
        
        // Almacenar datos en localStorage
        localStorage.setItem('token', authResult.token);
        localStorage.setItem('lastLogin', new Date().toISOString());
        localStorage.setItem('username', authResult.email || authResult.username);
        localStorage.setItem('userRole', authResult.role);
        localStorage.setItem('tenantKey', authResult.tenantKey);
        localStorage.setItem('allowedCourses', JSON.stringify(authResult.allowedCourses || ['Todos']));
        localStorage.setItem('allowedDimensions', JSON.stringify(authResult.allowedDimensions || []));
        
        // 🔍 LOGS DETALLADOS PARA DEBUGGING
        console.log("🔬 DEBUGGING - authResult completo:", JSON.stringify(authResult, null, 2));
        console.log("🔬 authResult.organization existe?", !!authResult.organization);
        console.log("🔬 authResult.organization valor:", authResult.organization);
        console.log("🔬 Propiedades disponibles en authResult:", Object.keys(authResult));
        
        // Almacenar información del usuario completa
        localStorage.setItem('userData', JSON.stringify(authResult));
        console.log("💾 userData guardado en localStorage");
        
        // Almacenar datos de la organización si están disponibles
        if (authResult.organization) {
          localStorage.setItem('organizationData', JSON.stringify(authResult.organization));
          console.log("🏢 ✅ Datos de organización almacenados:", authResult.organization.displayName);
          console.log("🎨 ✅ Logo URL:", authResult.organization.branding?.logoUrl);
          console.log("🔬 organizationData guardado:", JSON.stringify(authResult.organization, null, 2));
          
          // Crear tema personalizado basado en los datos de la organización
          const organizationTheme = {
            displayName: authResult.organization.displayName,
            branding: {
              logoUrl: authResult.organization.branding?.logoUrl || '/logocolegiosinnotas.png',
              faviconUrl: authResult.organization.branding?.faviconUrl || '/favicon.svg',
              colors: {
                primary: authResult.organization.branding?.colors?.primary || '#3B82F6',
                secondary: authResult.organization.branding?.colors?.secondary || '#10B981', 
                accent: authResult.organization.branding?.colors?.accent || '#F59E0B',
                background: authResult.organization.branding?.colors?.background || '#FFFFFF',
                text: authResult.organization.branding?.colors?.text || '#1F2937'
              },
              typography: {
                fontFamily: authResult.organization.branding?.typography?.fontFamily || 'Inter, system-ui, sans-serif',
                headingFont: authResult.organization.branding?.typography?.headingFont || 'Inter, system-ui, sans-serif'
              }
            },
            customization: {
              showWelcomeMessage: authResult.organization.customization?.showWelcomeMessage ?? true,
              welcomeMessage: authResult.organization.customization?.welcomeMessage || `Bienvenido al Sistema de Evaluación Integral - ${authResult.organization.displayName}`,
              footerText: authResult.organization.customization?.footerText || authResult.organization.displayName,
              supportEmail: authResult.organization.customization?.supportEmail || 'soporte@colegio.cl',
              supportPhone: authResult.organization.customization?.supportPhone || '+56 9 1234 5678'
            }
          };
          
          // Almacenar el tema personalizado
          localStorage.setItem('tenantTheme', JSON.stringify(organizationTheme));
          console.log("🎨 Tema personalizado creado para:", organizationTheme.displayName);
          
          // Actualizar título del documento con el nombre del tenant
          document.title = `${organizationTheme.displayName} - Sistema de Evaluación Integral`;
          
          // Forzar recarga del tema en TenantProvider
          window.dispatchEvent(new CustomEvent('tenantThemeUpdated', { 
            detail: { tenantKey: authResult.tenantKey, theme: organizationTheme }
          }));
        }
        
        setUsername(authResult.email || authResult.username);
        setRole(authResult.role as 'admin' | 'teacher');
        setAllowedCourses(authResult.allowedCourses || ['Todos']);
        
        // Asegurar que las dimensiones permitidas sean números
        const dimensionsAsNumbers = Array.isArray(authResult.allowedDimensions) 
          ? authResult.allowedDimensions.map((d: any) => typeof d === 'string' ? parseInt(d, 10) : Number(d)).filter((d: number) => !isNaN(d))
          : [];
        setAllowedDimensions(dimensionsAsNumbers);
        
        // Actualizar título del documento si no se actualizó antes
        if (!authResult.organization) {
          // Intentar obtener el displayName del tenant desde authResult o usar fallback
          let displayName = 'Sistema de Evaluación Integral';
          
          // Si hay displayName en authResult, usarlo
          if (authResult.displayName) {
            displayName = authResult.displayName;
          } else if (authResult.tenantKey === 'cpv_cl') {
            displayName = 'Colegio Puerto Varas';
          } else if (authResult.tenantKey) {
            // Intentar formatear el tenantKey como nombre
            displayName = authResult.tenantKey
              .replace(/_/g, ' ')
              .replace(/\./g, ' ')
              .split(' ')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
          
          document.title = `${displayName} - Sistema de Evaluación Integral`;
          
          // También actualizar el TenantContext
          const fallbackTheme = {
            displayName,
            branding: {
              logoUrl: '/logocolegiosinnotas.png',
              faviconUrl: '/favicon.svg',
              colors: {
                primary: '#3B82F6',
                secondary: '#10B981',
                accent: '#F59E0B',
                background: '#FFFFFF',
                text: '#1F2937'
              },
              typography: {
                fontFamily: 'Inter, system-ui, sans-serif',
                headingFont: 'Inter, system-ui, sans-serif'
              }
            },
            customization: {
              showWelcomeMessage: true,
              welcomeMessage: `Bienvenido al Sistema de Evaluación Integral - ${displayName}`,
              footerText: displayName,
              supportEmail: 'soporte@colegio.cl',
              supportPhone: '+56 9 1234 5678'
            }
          };
          
          localStorage.setItem('tenantTheme', JSON.stringify(fallbackTheme));
          window.dispatchEvent(new CustomEvent('tenantThemeUpdated', { 
            detail: { tenantKey: authResult.tenantKey, theme: fallbackTheme }
          }));
        }
        
        // 🔍 LOG PARA DEBUGGING - Confirmar dimensiones permitidas
        console.log("✅ Dimensiones permitidas establecidas para docente:", {
          username: authResult.email || authResult.username,
          role: authResult.role,
          allowedDimensions: authResult.allowedDimensions || [],
          shouldShowAllDimensions: (authResult.allowedDimensions || []).length === 0
        });
        
        // Actualizar timestamp de actividad
        updateUserActivity();
        
        console.log(`🎯 Login exitoso para ${authResult.username || authResult.email} (${authResult.role}) en ${authResult.tenantKey}`);

        // Cargar datos antes de mostrar el dashboard (mismo flujo que recarga de página)
        console.log("Cargando datos de estudiantes...");
        const generation = ++loadGenerationRef.current;
        const loaded = await loadProgress(authResult.token, generation);

        if (!loaded) {
          localStorage.removeItem('token');
          localStorage.removeItem('username');
          localStorage.removeItem('userRole');
          localStorage.removeItem('tenantKey');
          localStorage.removeItem('allowedCourses');
          localStorage.removeItem('allowedDimensions');
          localStorage.removeItem('organizationData');
          localStorage.removeItem('tenantTheme');
          localStorage.removeItem('userData');
          setToken(null);
          setUsername('');
          setRole('');
          setError("No se pudieron cargar los datos de estudiantes. Intente nuevamente.");
          return;
        }

        setAuthenticated(true);
        setActiveSection('dashboard');
      } else {
        // Incrementar contador de intentos fallidos
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        // Implementar bloqueo temporal después de varios intentos fallidos
        if (newAttempts >= 5) {
          const lockoutTime = new Date();
          lockoutTime.setMinutes(lockoutTime.getMinutes() + 15); // Bloqueo por 15 minutos
          setLockoutUntil(lockoutTime);
          setError("Demasiados intentos fallidos. La cuenta ha sido bloqueada temporalmente por 15 minutos.");
        } else {
          setError(authResult.message || "Credenciales inválidas. Intente nuevamente.");
        }
      }
    } catch (error: any) {
      console.error("Error detallado en login:", error);
      setError("Error al conectar con el servidor. Intente nuevamente más tarde.");
      
      // También incrementar contador de intentos en caso de error
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
    } finally {
      setIsLoading(false);
    }
  };

  // Modificar handleRegister para incluir validaciones de seguridad
  const handleRegister = async () => {
    try {
      setError("");
      setIsLoading(true);
      
      // Si el usuario no proporcionó un email, usar el nombre de usuario como email
      const email = username.includes('@') ? username : `${username}@cpv.cl`;
      
      // Validar entrada
      if (!username || !password) {
        setError("Por favor complete todos los campos");
        setIsLoading(false);
        return;
      }
      
      // Validar formato de correo
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError("Por favor ingrese un correo electrónico válido");
        setIsLoading(false);
        return;
      }
      
      // Validar fortaleza de contraseña
      const strength = evaluatePasswordStrength(password);
      if (strength === 'weak') {
        setError("La contraseña es demasiado débil. Debe tener al menos 8 caracteres e incluir mayúsculas, minúsculas, números y caracteres especiales.");
        setIsLoading(false);
        return;
      }
      
      console.log("Intentando registrar usuario:", { username, email });
      
      const response = await axios.post(
        `${API_BASE_URL}/create-user`, 
        { 
          username, 
          password, 
          email,
          csrfToken // Incluir CSRF token para prevenir ataques CSRF 
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '' // Incluir en headers también
          },
          timeout: 60000 // Aumentado para conexiones lentas
        }
      );
      
      console.log("Respuesta del servidor:", response.data);
      
      if (response.data.success) {
        alert("Usuario creado exitosamente. Por favor, inicia sesión.");
        setIsRegistering(false);
        // Limpiar los campos después de registro exitoso
        setUsername("");
        setPassword("");
        setPasswordStrength(null);
      } else {
        setError(response.data.message || "Error al crear el usuario");
      }
    } catch (error: any) {
      console.error("Error detallado en registro:", {
        mensaje: error.message,
        codigo: error.code,
        respuesta: error.response?.data,
        estado: error.response?.status,
        stack: error.stack
      });
      setError(error.response?.data?.message || "Error al conectar con el servidor");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRegister = () => {
    setIsRegistering(!isRegistering);
    setError("");
  };

  const handleForgotPassword = async () => {
    try {
      setError("");
      setIsLoading(true);
      
      if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
        setError("Por favor, ingrese un correo electrónico válido");
        setIsLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.trim() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.message || "No se pudo enviar el correo. Intente nuevamente.");
      } else {
        setForgotPasswordSuccess(true);
      }
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error al procesar la solicitud:", error);
      setError("Error al procesar la solicitud. Intente nuevamente.");
      setIsLoading(false);
    }
  };

  const handleChangeOwnPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetNewPassword.length < 6) {
      setResetMessage("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetMessage("Las contraseñas no coinciden");
      return;
    }
    setChangingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/me/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token || localStorage.getItem('token')}` },
        body: JSON.stringify({ currentPassword: resetCurrentPassword, newPassword: resetNewPassword })
      });
      const data = await response.json().catch(() => ({}));
      setResetDone(response.ok && !!data.success);
      setResetMessage(data.message || (response.ok ? "Contraseña actualizada" : "No se pudo actualizar la contraseña"));
    } catch {
      setResetMessage("Error de conexión. Intente nuevamente.");
    }
    setChangingPassword(false);
  };

  const closeResetModal = () => {
    setShowChangePassword(false);
    setResetCurrentPassword("");
    setResetNewPassword("");
    setResetConfirmPassword("");
    setResetMessage("");
    setResetDone(false);
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordSuccess(false);
    setError("");
  };

  // Modificar handleCourseSelect para incluir el ordenamiento por apellido
  const handleCourseSelect = useCallback((course: string) => {
    setSelectedCourse(course);
    const filteredStudents = course 
      ? allStudents.filter(student => student.course === course)
      : allStudents;

    // Ordenar los estudiantes por primer apellido
    const sortedStudents = [...filteredStudents].sort((a, b) => {
      const lastNameA = getFirstLastName(a.name);
      const lastNameB = getFirstLastName(b.name);
      return lastNameA.localeCompare(lastNameB);
    });

    setStudents(sortedStudents);
    
    if (!selectedStudentIds.some(id => id && findStudentById(sortedStudents, id))) {
      setSelectedStudentIds(sortedStudents.length > 0 && sortedStudents[0].id ? [sortedStudents[0].id] : []);
    }
  }, [allStudents, getFirstLastName, selectedStudentIds]);

  // 🔍 MINIMAL DEBUG: Only log significant issues
  useEffect(() => {
    if (selectedStudentIds.length > 5) {
      console.warn('⚠️  More than 5 students selected - possible multi-select issue:', selectedStudentIds.length);
    }
  }, [selectedStudentIds]);

  const handleStudentSelect = (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => {
    if (!id) return;
    const normalizedId = normalizeStudentId(id);
    console.log('🔍 handleStudentSelect llamado con:', { 
      id: normalizedId, 
      idType: typeof normalizedId
    });
    
    const targetStudent = allStudents.find(s => normalizeStudentId(s.id) === normalizedId);
    console.log('🔍 Estudiante encontrado en selección:', targetStudent ? { id: targetStudent.id, name: targetStudent.name } : "NO ENCONTRADO");
    
    // 🚨 Check for duplicate students with same ID
    const studentsWithSameId = allStudents.filter(s => normalizeStudentId(s.id) === normalizedId);
    if (studentsWithSameId.length > 1) {
      console.error('🚨 CRITICAL: Multiple students with same ID found:', studentsWithSameId.map(s => ({ id: s.id, name: s.name, course: s.course })));
    }
    
    if (event.ctrlKey || event.metaKey) {
      setSelectedStudentIds(prev => {
        const isAlreadySelected = prev.some(sid => normalizeStudentId(sid) === normalizedId);
        if (isAlreadySelected) {
          return prev.filter(sid => normalizeStudentId(sid) !== normalizedId);
        } else {
          return [...prev, normalizedId];
        }
      });
    } else {
      setSelectedStudentIds([normalizedId]);
    }
  };

  // 🎯 SISTEMA DE CAMBIOS GRANULARES - Solo rastrear skills específicos que cambiaron
  interface GranularSkillChange {
    studentId: number | string; // Support both numeric IDs and string RUTs
    dimensionId: number;
    skillId: number;
    newLevel: number | null; // null significa eliminar el skill
    notEvaluated?: boolean; // true indica que la habilidad no ha sido evaluada
    timestamp: number; // Para resolver conflictos
  }

  interface StudentGranularChanges {
    id: number | string; // Support both numeric IDs and string RUTs
    skillChanges: GranularSkillChange[]; // Solo los skills que cambiaron
    qualitativeDescription?: string;
  }

  // 🔄 Cambiar de changedStudents a granularChanges para tracking específico
  const [granularChanges, setGranularChanges] = useState<{[key: string]: StudentGranularChanges}>({});



  // 🎯 FUNCIÓN GRANULAR - Solo registrar el skill específico que cambió
  const handleLevelChange = (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => {
    if (studentId === undefined || studentId === null) {
      console.error("❌ ERROR: studentId is undefined or null in handleLevelChange");
      return;
    }

    const normalizedStudentId = normalizeStudentId(studentId);
    console.log("🔍 handleLevelChange llamado con:", {
      studentId: normalizedStudentId,
      studentIdType: typeof normalizedStudentId,
      dimensionId,
      skillId,
      levelIndex
    });

    // 🔧 NUEVO: Si levelIndex es -1 (para compatibilidad con botones de reiniciar), convertir a null
    let actualLevelIndex = levelIndex;
    if (levelIndex === -1) {
      actualLevelIndex = null;
    }
    
    // Debug: Verificar si el estudiante existe
    const student = students.find(s => normalizeStudentId(s.id) === normalizedStudentId);
    console.log("🔍 Estudiante encontrado:", student ? { id: student.id, name: student.name } : "NO ENCONTRADO");
    
    if (!student) {
      console.error("❌ ERROR: Estudiante no encontrado con ID:", normalizedStudentId);
      return;
    }
    
    // 1. Actualizar UI de manera optimista
    updateStudentSkillLocally(normalizedStudentId, dimensionId, skillId, actualLevelIndex);

    // 2. 🎯 REGISTRAR SOLO EL CAMBIO GRANULAR ESPECÍFICO
    setGranularChanges(prev => {
      const studentKey = normalizedStudentId.toString();
      const currentStudent = prev[studentKey] || {
        id: normalizedStudentId,
        skillChanges: []
      };

      // Crear el cambio granular específico
      const granularChange: GranularSkillChange = {
        studentId,
        dimensionId,
        skillId,
        newLevel: actualLevelIndex,
        timestamp: Date.now()
      };
      
      // Eliminar cambios anteriores del mismo skill (solo el más reciente cuenta)
      const filteredChanges = currentStudent.skillChanges.filter(
        change => !(change.dimensionId === dimensionId && change.skillId === skillId)
      );
      
      // Agregar el nuevo cambio
      const updatedStudent = {
        ...currentStudent,
        skillChanges: [...filteredChanges, granularChange]
      };
      
      console.log('🎯 Granular change registered:', {
        studentId,
        totalChanges: updatedStudent.skillChanges.length,
        thisChange: granularChange
      });
      
      return {
        ...prev,
        [studentKey]: updatedStudent
      };
    });
    
    // 3. Marcar cambios no guardados
    setUnsavedChanges(true);
  };

  // Función auxiliar para actualizar el estado local de la habilidad
  const updateStudentSkillLocally = (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => {
    if (studentId === undefined || studentId === null) {
      console.error("❌ ERROR: studentId is undefined or null in updateStudentSkillLocally");
      return;
    }

    const normalizedStudentId = normalizeStudentId(studentId);
    console.log('🔍 updateStudentSkillLocally llamado con:', { 
      studentId: normalizedStudentId, 
      studentIdType: typeof normalizedStudentId,
      dimensionId, 
      skillId, 
      levelIndex 
    });
    
    // Actualizar el estado de students
    setStudents(prevStudents => {
      return prevStudents.map(student => {
        if (normalizeStudentId(student.id) === normalizedStudentId) {
          console.log('✅ Estudiante encontrado para actualizar:', { id: student.id, name: student.name });
          const newSkillLevels = [...(student.skillLevels || [])];
          const existingIndex = newSkillLevels.findIndex(
            sl => sl.dimensionId === dimensionId && sl.skillId === skillId
          );

          // Si el nivel seleccionado es el mismo que ya está, lo removemos
          if (existingIndex >= 0 && newSkillLevels[existingIndex].level === levelIndex) {
            newSkillLevels.splice(existingIndex, 1);
          } else {
            // Si no existe o es diferente, lo actualizamos o agregamos
            if (existingIndex >= 0) {
              newSkillLevels[existingIndex] = {
                dimensionId,
                skillId,
                level: levelIndex
              };
            } else if (levelIndex !== null) {
              newSkillLevels.push({
                dimensionId,
                skillId,
                level: levelIndex
              });
            }
          }

          return {
            ...student,
            skillLevels: newSkillLevels
          };
        }
        return student;
      });
    });

    // También actualizar en allStudents para consistencia
    setAllStudents(prevAllStudents => {
      return prevAllStudents.map(student => {
        if (normalizeStudentId(student.id) === normalizedStudentId) {
          const newSkillLevels = [...(student.skillLevels || [])];
          const existingIndex = newSkillLevels.findIndex(
            sl => sl.dimensionId === dimensionId && sl.skillId === skillId
          );

          if (existingIndex >= 0 && newSkillLevels[existingIndex].level === levelIndex) {
            newSkillLevels.splice(existingIndex, 1);
          } else {
            if (existingIndex >= 0) {
              newSkillLevels[existingIndex] = {
                dimensionId,
                skillId,
                level: levelIndex
              };
            } else if (levelIndex !== null) {
              newSkillLevels.push({
                dimensionId,
                skillId,
                level: levelIndex
              });
            }
          }

          return {
            ...student,
            skillLevels: newSkillLevels
          };
        }
        return student;
      });
    });

    // 🎯 Sistema granular ya maneja el tracking en handleLevelChange
    // No se necesita código adicional aquí
  };

  // 🚫 FUNCIÓN PARA MARCAR HABILIDAD COMO "NO EVALUADA"
  const handleNotEvaluatedChange = (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => {
    console.log("🚫 handleNotEvaluatedChange llamado:", { studentId, dimensionId, skillId, notEvaluated });
    
    const normalizedStudentId = normalizeStudentId(studentId);
    const student = students.find(s => normalizeStudentId(s.id) === normalizedStudentId);
    if (!student) {
      console.error("❌ ERROR: Estudiante no encontrado para marcar como no evaluada:", normalizedStudentId);
      return;
    }

    // Actualizar el estado local
    setStudents(prevStudents => {
      return prevStudents.map(s => {
        if (s.id === studentId) {
          const newSkillLevels = [...(s.skillLevels || [])];
          const existingIndex = newSkillLevels.findIndex(
            sl => sl.dimensionId === dimensionId && sl.skillId === skillId
          );

          if (existingIndex >= 0) {
            // Si existe, actualizar el campo notEvaluated
            newSkillLevels[existingIndex] = {
              ...newSkillLevels[existingIndex],
              notEvaluated: notEvaluated,
              // Si se marca como no evaluada, limpiar el nivel
              level: notEvaluated ? null : newSkillLevels[existingIndex].level
            };
          } else if (notEvaluated) {
            // Si no existe y se está marcando como no evaluada, crear nuevo
            newSkillLevels.push({
              dimensionId,
              skillId,
              level: null,
              notEvaluated: true
            });
          }

          return { ...s, skillLevels: newSkillLevels };
        }
        return s;
      });
    });

    // También actualizar en allStudents
    setAllStudents(prevAllStudents => {
      return prevAllStudents.map(s => {
        if (s.id === studentId) {
          const newSkillLevels = [...(s.skillLevels || [])];
          const existingIndex = newSkillLevels.findIndex(
            sl => sl.dimensionId === dimensionId && sl.skillId === skillId
          );

          if (existingIndex >= 0) {
            newSkillLevels[existingIndex] = {
              ...newSkillLevels[existingIndex],
              notEvaluated: notEvaluated,
              level: notEvaluated ? null : newSkillLevels[existingIndex].level
            };
          } else if (notEvaluated) {
            newSkillLevels.push({
              dimensionId,
              skillId,
              level: null,
              notEvaluated: true
            });
          }

          return { ...s, skillLevels: newSkillLevels };
        }
        return s;
      });
    });

    // Registrar cambio granular con campo notEvaluated explícito
    setGranularChanges(prev => {
      const studentKey = studentId.toString();
      const currentStudent = prev[studentKey] || { id: studentId, skillChanges: [] };

      const granularChange: GranularSkillChange = {
        studentId,
        dimensionId,
        skillId,
        newLevel: notEvaluated ? null : null, // Si es no evaluada, el nivel es null
        notEvaluated: notEvaluated, // Campo explícito para indicar si es "no evaluada"
        timestamp: Date.now()
      };

      const filteredChanges = currentStudent.skillChanges.filter(
        change => !(change.dimensionId === dimensionId && change.skillId === skillId)
      );

      return {
        ...prev,
        [studentKey]: {
          ...currentStudent,
          skillChanges: [...filteredChanges, granularChange]
        }
      };
    });

    setUnsavedChanges(true);
  };

  // Agregar estos estados al inicio del componente
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
    show: boolean;
  }>({
    type: 'success',
    message: '',
    show: false
  });

  // 🎯 FUNCIÓN GRANULAR: Solo enviar cambios específicos al backend
  const saveProgress = async () => {
    try {
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      // 🎯 Transformar cambios granulares al formato esperado por el backend
      const granularData = Object.values(granularChanges)
        .filter(studentData => studentData.id && studentData.id !== 0) // 🔧 Filtrar estudiantes sin ID válido
        .map(studentData => ({
          id: normalizeStudentId(studentData.id),
          // 🎯 SOLO enviar los cambios específicos, no todos los skillLevels
          skillChanges: studentData.skillChanges
            .filter(change => change.studentId && change.studentId !== 0) // 🔧 FIX: Filtrar skillChanges con studentId null
            .map(change => ({
              studentId: change.studentId, // 🔧 FIX: Incluir studentId en cada cambio
              dimensionId: change.dimensionId,
              skillId: change.skillId,
              newLevel: change.newLevel,
              notEvaluated: change.notEvaluated, // 🚫 Incluir campo notEvaluated
              timestamp: change.timestamp
            })),
          qualitativeDescription: studentData.qualitativeDescription || ""
        }))
        .filter(studentData =>
          studentData.skillChanges.length > 0 ||
          (studentData.qualitativeDescription !== undefined && studentData.qualitativeDescription !== "")
        ); // 🔧 FIX: Incluir estudiantes con cambios en descripción cualitativa

      if (granularData.length === 0) {
        setNotification({
          type: 'success',
          message: 'No hay cambios para guardar',
          show: true
        });
        setTimeout(() => {
          setNotification(prev => ({ ...prev, show: false }));
        }, 3000);
        return;
      }

      console.log('🎯 Sending granular changes to backend:', {
        studentsAffected: granularData.length,
        totalSkillChanges: granularData.reduce((sum, student) => sum + student.skillChanges.length, 0),
        changes: granularData
      });

      // ✨ El modal se encarga de todo el proceso de guardado granular
      setShowSaveProgressModal(true);

    } catch (error: any) {
      console.error("Error preparando guardado granular:", error);
      setNotification({
        type: 'error',
        message: "Error al preparar los datos granulares para guardar",
        show: true
      });
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 5000);
    }
  };

  // ✅ Callback cuando el modal completa exitosamente  
  const handleSaveProgressComplete = async (results: any) => {
    setShowSaveProgressModal(false);
    
    if (results.success) {
      // 🎯 Limpiar cambios granulares sin guardar
      setGranularChanges({});
      setUnsavedChanges(false);
      
      // 🔇 ELIMINADA: Notificación redundante - el SaveProgressModal ya maneja todo el feedback
      // El modal ya muestra notificaciones completas y detalladas durante todo el proceso
      
      // HTTP-only mode: Save completed successfully
      if (socketContext.isConnected) {
        console.log('🔄 HTTP-only: Save completed successfully');
      }
      
      // 🚫 ELIMINADO: No recargar datos después del guardado exitoso
      // El estado local ya tiene los cambios aplicados correctamente
      // if (token) {
      //     await loadProgress(token);
      // }
    }
    
    // 🔇 ELIMINADAS: Todas las notificaciones adicionales redundantes
    // El SaveProgressModal maneja completamente la comunicación con el usuario
  };

  // ❌ Callback cuando el modal tiene errores
  const handleSaveProgressError = (error: string) => {
    setShowSaveProgressModal(false);

    // 🔍 MEJORAR MENSAJE DE ERROR PARA JSON INVÁLIDO
    let errorMessage = error || "Error al guardar los cambios";

    if (error.includes('INVALID_JSON_RESPONSE')) {
      errorMessage = "⚠️ Error de comunicación con el servidor. El servidor devolvió una respuesta inesperada. Por favor, inténtelo nuevamente o contacte al soporte técnico si el problema persiste.";
    } else if (error.includes('timeout')) {
      errorMessage = "⏰ Tiempo agotado al guardar los cambios. Por favor, verifique su conexión a internet e inténtelo nuevamente.";
    } else if (error.includes('network')) {
      errorMessage = "🌐 Error de conexión. Por favor, verifique su conexión a internet e inténtelo nuevamente.";
    }

    setNotification({
      type: 'error',
      message: errorMessage,
      show: true
    });

    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 7000); // Tiempo más largo para errores técnicos
  };

  // Modificar handleDescriptionChange para mantener los skillLevels existentes y corregir errores
  // 🎯 FUNCIÓN GRANULAR: handleDescriptionChange actualizada para sistema granular
  const handleDescriptionChange = (studentId: number | string, value: string) => {
    // Actualizar el estado local
    setStudents(prevStudents =>
      prevStudents.map(student =>
        studentIdsMatch(student.id, studentId)
          ? { ...student, qualitativeDescription: value }
          : student
      )
    );
    
    // 🎯 Actualizar granularChanges para description
    setGranularChanges(prev => {
      const studentKey = String(studentId);
      const currentStudent = prev[studentKey] || { 
        id: studentId, 
        skillChanges: []
      };
      
      // Actualizar la descripción cualitativa
      const updatedStudent = {
        ...currentStudent,
        qualitativeDescription: value
      };
      
      return {
        ...prev,
        [studentKey]: updatedStudent
      };
    });
    
    setUnsavedChanges(true);
  };

  // Función removida - ya no es necesaria en la nueva arquitectura

  const exportToExcel = useCallback((data?: any[], filename?: string) => {
    const dataToExport = data || students.map((student) => {
      const age = calculateAge(student.birthDate);
      const studentData: { [key: string]: any } = { 
        Estudiante: student.name,
        Edad: age,
        Curso: student.course
      };

      // Usar los skillLevels para obtener los niveles
      student.skillLevels.forEach((skillLevel) => {
        const dimension = dimensionsTemplate.find(d => d.id === skillLevel.dimensionId);
        const skill = dimension?.skills.find(s => s.id === skillLevel.skillId);
        if (dimension && skill) {
          studentData[`${dimension.name} - ${skill.name}`] = 
            skillLevel.level !== null ? skill.levels[skillLevel.level] : 'No evaluado';
        }
      });

      studentData["Descripción Cualitativa"] = student.qualitativeDescription || '';
      return studentData;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Estudiantes");

    const today = new Date();
    const dateStr = `${today.getFullYear()}-${(today.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
    XLSX.writeFile(workbook, filename || `Seguimiento_Estudiantes_${dateStr}.xlsx`);
  }, [students]);

  // Función movida después de exportToPDF

  // Chart generation function removed as Visualización section has been eliminated

  const [showInstructions, setShowInstructions] = useState(false);
  const [showAppInfo, setShowAppInfo] = useState(false);
  
  // Nuevo estado para el modal combinado y control de pestañas
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [activeHelpTab, setActiveHelpTab] = useState<'instructions' | 'about' | 'updates' | 'status'>('instructions');
  
  // Estado para el dropdown del usuario
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Cerrar dropdown del usuario al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserDropdown) {
        const target = event.target as Element;
        if (!target.closest('.user-dropdown')) {
          setShowUserDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);



  const [searchTerm, setSearchTerm] = useState("");

  // Permisos de visualización de dimensiones (incluye Trabajo colaborativo para todos)
  const canViewDimension = useCallback((dimensionId: number) => {
    // Restringir "Amor a Dios y sentido Cristiano" solo para tenant CPV
    if (dimensionId === 11 && tenantKey !== 'cpv.cl' && tenantKey !== 'cpv_cl') {
      return false;
    }

    // Trabajo colaborativo debe estar visible para todos los roles
    if (dimensionId === 12) {
      return true;
    }

    // Admin puede ver todo
    if (role === 'admin') {
      return true;
    }

    // Si no hay restricciones configuradas, mostrar todas
    if (allowedDimensions.length === 0) {
      return true;
    }

    // Convertir todo a números para la comparación (por si vienen como strings desde la BD)
    return allowedDimensions.map(Number).includes(Number(dimensionId));
  }, [allowedDimensions, role, tenantKey]);

  // Definir el tipo de las dimensiones
  interface DimensionCategory {
    name: string;
    color: string;
    dimensions: {
      id: number;
      name: string;
      component: React.FC<any>; // Usa 'any' temporalmente, pero es mejor definir una interfaz específica
      icon: IconType;
    }[];
  }

  // Filtrar categorías según tenant usando useMemo
  const dimensionCategories = useMemo((): DimensionCategory[] => {
    const allCategories: DimensionCategory[] = [
    {
      name: "Exploración de uno mismo",
      color: "#4A90E2",
      dimensions: [
        { id: 1, name: "Autoconciencia y voluntad", component: AutoconcienciaVoluntad, icon: FaBrain },
        { id: 2, name: "Habilidades pensamiento", component: HabilidadesPensamiento, icon: FaQuestionCircle },
        { id: 3, name: "Habilidades comunicacionales en el lenguaje español", component: HabilidadesComunicacionales, icon: FaComments },
        { id: 4, name: "Corporalidad y Movimiento", component: CorporalidadMovimiento, icon: FaRunning },
        { id: 5, name: "Lenguaje Artístico", component: LenguajeArtistico, icon: FaPalette },
        { id: 6, name: "Motivación y disposición hacia el aprendizaje", component: MotivacionAprendizaje, icon: FaBook },
      ],
    },
    {
      name: "Exploración del mundo",
      color: "#50E3C2",
      dimensions: [
        { id: 7, name: "Encuentro con la Naturaleza y Sociedad", component: EncuentroNaturaleza, icon: FaTree },
        { id: 8, name: "Habilidades de relación y conciencia social", component: HabilidadesSociales, icon: FaUserFriends },
        { id: 9, name: "Pensamiento matemático", component: PensamientoMatematico, icon: FaCalculator },
        { id: 10, name: "Acercamiento a la lengua inglesa", component: LenguaInglesa, icon: FaLanguage },
      ],
    },
    {
      name: "Exploración de lo Trascendente",
      color: "#F5A623",
      dimensions: [
        { id: 11, name: "Amor a Dios y sentido Cristiano", component: AmorDios, icon: FaPray },
      ],
    },
    {
      name: "Descripción Cualitativa",
      color: "#D0021B",
      dimensions: [
        { id: 12, name: "Trabajo colaborativo", component: DescripcionCualitativa as React.FC<any>, icon: FaPen },
      ],
    },
  ];

  // Filtrar categorías completas para tenants no-CPV
  return allCategories.filter(category => {
    // Si no es tenant CPV, excluir la categoría "Exploración de lo Trascendente" completa
    if (category.name === "Exploración de lo Trascendente" && tenantKey !== 'cpv.cl' && tenantKey !== 'cpv_cl') {
      return false;
    }
    return true;
  });
}, [tenantKey]);

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(cat => cat !== categoryName)
        : [...prev, categoryName]
    );
  };

  // Cambiamos el estado de la barra lateral a un enum
  const [sidebarState, setSidebarState] = useState<'hidden' | 'minimized' | 'expanded'>('hidden');
  
  const toggleSidebar = () => {
    setSidebarState(prevState => {
      // Solo permitir toggle si estamos en la sección de formularios Y no es admin de la plataforma
      if (activeSection === 'formularios' && username !== SYSTEM_ADMIN_EMAIL) {
      switch (prevState) {
        case 'hidden':
          return 'minimized';
        case 'minimized':
          return 'hidden';
        case 'expanded':
          return 'minimized';
        default:
          return 'minimized';
        }
      } else {
        // En otras secciones o para admin de la plataforma, el sidebar debe permanecer oculto
        return 'hidden';
      }
    });
  };

  // Modificar la función de filtrado para manejar valores undefined
  const filteredStudents = useMemo(() => {
    return students.filter((student) =>
      (student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (student.skillLevels.some(skillLevel => {
        const dimension = dimensionsTemplate.find(d => d.id === skillLevel.dimensionId);
        const skill = dimension?.skills.find(s => s.id === skillLevel.skillId);
        return skill?.name.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
      }))
    );
  }, [students, searchTerm]);

  const [studentBirthDate, setStudentBirthDate] = useState<string>("");

  const handleStudentBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStudentBirthDate(e.target.value);
    // Aquí puedes agregar lógica para calcular la edad y actualizar el estado del estudiante
  };

  const [viewMode, setViewMode] = useState<'student' | 'skill'>('student');

  const handleViewModeChange = (mode: 'student' | 'skill') => {
    setViewMode(mode);
  };

  const handleSkillSelect = (skill: Skill) => {
    setSelectedDimension(skill.id);
  };

  const handleSkillLevelChange = (studentId: number, levelIndex: number) => {
    setStudents(prevStudents => 
      prevStudents.map(student => {
        if (student.id === studentId && selectedDimension !== null) {
          const newSkillLevels = [...student.skillLevels];
          const existingIndex = newSkillLevels.findIndex(
            sl => sl.skillId === selectedDimension
          );

          if (existingIndex >= 0) {
            newSkillLevels[existingIndex].level = levelIndex;
          } else {
            newSkillLevels.push({
              dimensionId: selectedDimension,
              skillId: selectedDimension,
              level: levelIndex
            });
          }

          return {
            ...student,
            skillLevels: newSkillLevels
          };
        }
        return student;
      })
    );
  };

  // ================== MODERN PDF GENERATION ==================
  // PDF Export Functions (Enhanced version with images, sidebar, professional design)
  const APP_VERSION = "2.0.2";

  interface TeacherInfo {
    course: string;
    spanishTeacher: string;
    englishTeacher: string;
  }

  const getTeacherInfo = (course: string): TeacherInfo | undefined => {
    const teachersInfoCurrent = [
      { course: "MMA", spanishTeacher: "Ximena Rodríguez", englishTeacher: "Rossana Díaz" },
      { course: "MMB", spanishTeacher: "Carmen Gloria Long", englishTeacher: "Pilar Plaza de los Reyes" },
      { course: "MMC", spanishTeacher: "Josefa Crovetto", englishTeacher: "Camila de la Sotta" },
      { course: "MMD", spanishTeacher: "Carolina Alday", englishTeacher: "María Jesús Valdivieso" },
      { course: "PKA", spanishTeacher: "Carola Meersohn", englishTeacher: "Teresa Echeverría" },
      { course: "PKC", spanishTeacher: "Vanessa Almonacid", englishTeacher: "Marcela Moreno" },
      { course: "PKB", spanishTeacher: "Pía Pequeño", englishTeacher: "Francisca Portaluppi" },
      { course: "KA", spanishTeacher: "Paulina Schipmann", englishTeacher: "Carolina Krause" },
      { course: "KB", spanishTeacher: "Constanza Moguillansky", englishTeacher: "Camila Álvarez" },
      { course: "KC", spanishTeacher: "Guacolda Martínez", englishTeacher: "Camila Mejías" },
      { course: "1A", spanishTeacher: "María Paz Urzúa", englishTeacher: "Magdalena Undurraga" },
      { course: "1B", spanishTeacher: "Danitza Munjin", englishTeacher: "Rebeca Pizarro" },
      { course: "1C", spanishTeacher: "Tamara Torres", englishTeacher: "María Carolina Alfaro" },
      { course: "2A", spanishTeacher: "Soledad Parada", englishTeacher: "Magdalena Latorre" },
      { course: "2B", spanishTeacher: "Dolores Ugalde", englishTeacher: "Josefina Durán" },
      { course: "2C", spanishTeacher: "Macarena Gutiérrez", englishTeacher: "Catalina Werner" }
    ];

    const courseMap: { [key: string]: string } = {
      'Nivel medio mayor A': 'MMA',
      'Nivel medio mayor B': 'MMB',
      'Nivel medio mayor C': 'MMC',
      'Nivel medio mayor D': 'MMD',
      'Prekínder A': 'PKA',
      'Prekínder B': 'PKB',
      'Prekínder C': 'PKC',
      'Kínder A': 'KA',
      'Kínder B': 'KB',
      'Kínder C': 'KC',
      'Primero básico A': '1A',
      'Primero básico B': '1B',
      'Primero básico C': '1C',
      'Segundo básico A': '2A',
      'Segundo básico B': '2B',
      'Segundo básico C': '2C'
    };

    const shortCourse = courseMap[course];
    return teachersInfoCurrent.find(t => t.course === shortCourse);
  };

  const wrapText = (text: string, maxWidth: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  };

  const calculateAgeForPDF = (birthDate: string): string => {
    const birth = parseDate(birthDate);
    if (!birth) return "No disponible";
    
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    if (today.getDate() < birth.getDate()) {
      months--;
      if (months < 0) {
        years--;
        months += 12;
      }
    }
    
    // Validar que la edad sea razonable
    if (years < 0 || years > 120) {
      console.warn(`⚠️ Edad calculada inválida: ${years} años para fecha: ${birthDate}`);
      return "No disponible";
    }
    
    if (years === 0) {
      return `${months} meses`;
    } else if (months === 0) {
      return `${years} año${years > 1 ? 's' : ''}`;
    } else {
      return `${years} año${years > 1 ? 's' : ''} y ${months} mes${months > 1 ? 'es' : ''}`;
    }
  };

  const getCicloText = (course: string): string => {
    // Verificar si es Colegio Puerto Varas
    const isCPV = () => {
      try {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const user = JSON.parse(userData);
          return user.tenantKey === 'cpv_cl' || user.email?.includes('@cpv.cl');
        }
        
        const organizationData = localStorage.getItem('organizationData');
        if (organizationData) {
          const org = JSON.parse(organizationData);
          return org.tenantKey === 'cpv_cl';
        }
        
        // Fallback: revisar URL o dominio
        return window.location.hostname.includes('cpv') || 
               window.location.href.includes('cpv');
      } catch (error) {
        console.warn('Error verificando tenantKey:', error);
        return false;
      }
    };
    
    // Si no es CPV, no mostrar texto de ciclo
    if (!isCPV()) {
      return '';
    }
    
    // ✅ LÓGICA ORIGINAL SOLO PARA CPV
    if (course.includes('Nivel medio mayor')) return 'PRIMER CICLO';
    if (course.includes('Prekínder') || course.includes('Kínder')) return 'SEGUNDO CICLO';
    if (course.includes('Primero básico') || course.includes('Segundo básico')) return 'TERCER CICLO';
    return 'CICLO';
  };

  const exportToPDF = useCallback(async (student: Student, chartImage?: string) => {
    const pdf = new jsPDF({
      compress: false, // Desactivar compresión para máxima calidad
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: false, // Incluir todas las fuentes para mejor calidad de texto
      orientation: 'portrait',
      floatPrecision: 16 // Máxima precisión para mejor calidad
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const cornerRadius = 5;

    // Versión en esquina
    pdf.setFontSize(8);
    pdf.setTextColor(150);
    pdf.text(`v${APP_VERSION}`, pageWidth - 20, pageHeight - 10);
    
    // Función para añadir el fondo y barra lateral
    const addBackgroundAndSidebar = () => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Obtener colores de branding personalizados
      const brandingColors = getTenantBrandingColors();
      const primaryColor = brandingColors.primary;

      const blueBarWidth = 20;
      pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b); // Color primario personalizado
      pdf.rect(0, 0, blueBarWidth, pageHeight, 'F');

      const pinkColor = [246, 206, 30];
      pdf.setFontSize(14);
      pdf.setTextColor(pinkColor[0], pinkColor[1], pinkColor[2]);
      pdf.setFont('helvetica', 'bold');

      const cicloSubTextY = pageHeight - 25;
      const lineY = cicloSubTextY - 5;
      
      const cicloText = getCicloText(student.course);
      const xPosition = blueBarWidth / 2;
      
      // Solo renderizar todo el bloque de ciclo si no está vacío (es decir, solo para CPV)
      if (cicloText.length > 0) {
        const letters = cicloText.split('');
        const letterSpacing = 5.5;
        
        const totalTextHeight = (letters.length - 1) * letterSpacing;
        let yPosition = lineY - totalTextHeight - 5;
        
        letters.forEach(letter => {
          const textWidth = pdf.getStringUnitWidth(letter) * pdf.getFontSize() / pdf.internal.scaleFactor;
          pdf.text(letter, xPosition - (textWidth / 2), yPosition);
          yPosition += letterSpacing;
        });

        // Dibujar la línea horizontal (solo para CPV)
        pdf.setDrawColor(pinkColor[0], pinkColor[1], pinkColor[2]);
        pdf.setLineWidth(0.5);
        pdf.line(xPosition - 5, lineY, xPosition + 5, lineY);

        // Escribir "ciclo" en posición fija (solo para CPV)
        pdf.setFontSize(12);
        const cicloSubText = 'ciclo';
        const cicloSubWidth = pdf.getStringUnitWidth(cicloSubText) * pdf.getFontSize() / pdf.internal.scaleFactor;
        pdf.text(cicloSubText, xPosition - (cicloSubWidth / 2), cicloSubTextY);
      }
    };

    // Función para añadir header
    const addHeader = () => {
      pdf.setFontSize(12); // Aumentado de 10 a 12
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Nombre:', 30, 16); // Bajado ligeramente para centrar
      pdf.setFont('helvetica', 'normal');
      pdf.text(student.name, 55, 16); // Ajustado posición X
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Curso:', 30, 26); // Aumentado espacio entre líneas
      pdf.setFont('helvetica', 'normal');
      pdf.text(student.course, 55, 26); // Ajustado posición X
      

      try {
        // Optimización: Reducir calidad del logo para menor tamaño - Logo personalizado por tenant
        const tenantLogo = getTenantLogo();
        loadImageAsBase64(tenantLogo)
          .then(logoBase64 => {
            pdf.addImage(logoBase64, 'PNG', pageWidth - 36, 10, 16, 19, '', 'FAST');
          })
          .catch(error => {
            console.error('Error al cargar logo del header:', error);
            // Continuar sin logo si falla
          });
      } catch (error) {
        console.error('Error al agregar el logo al header:', error);
        // Continuar sin logo si falla
      }
    };

    // Portada
    addBackgroundAndSidebar();
    
    // Obtener colores de branding para la barra horizontal
    const brandingColors = getTenantBrandingColors();
    const secondaryColor = brandingColors.secondary;
    
    pdf.setFillColor(secondaryColor.r, secondaryColor.g, secondaryColor.b); // Color secundario personalizado
    pdf.rect(0, pageHeight - 15, pageWidth, 5, 'F');

    pdf.setFontSize(28);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    
    const titleLine1 = "Informe Pedagógico";
    const titleLine2 = `Integral ${new Date().getFullYear()}`;
    
    const titleWidth1 = pdf.getStringUnitWidth(titleLine1) * pdf.getFontSize() / pdf.internal.scaleFactor;
    const titleWidth2 = pdf.getStringUnitWidth(titleLine2) * pdf.getFontSize() / pdf.internal.scaleFactor;
    
    const titleX1 = (pageWidth - titleWidth1) / 2;
    const titleX2 = (pageWidth - titleWidth2) / 2;
    
    pdf.text(titleLine1, titleX1, 35);
    pdf.text(titleLine2, titleX2, 50);

    try {
      // Optimización: Logo de portada con compresión moderada - Logo personalizado por tenant
      const tenantLogo = getTenantLogo();
      loadImageAsBase64(tenantLogo)
        .then(logoBase64 => {
          pdf.addImage(logoBase64, 'PNG', pageWidth - 45, 20, 30, 35, '', 'MEDIUM');
        })
        .catch(error => {
          console.error('Error al cargar logo de portada:', error);
          // Continuar sin logo si falla
        });
    } catch (error) {
      console.error('Error al agregar el logo a la portada:', error);
      // Continuar sin logo si falla
    }

    // Información del estudiante en portada
    pdf.setFontSize(14); // Aumentado de 12 a 14
    const yCenter = pageHeight / 2;
    const lineSpacing = 10; // Aumentado de 8 a 10 para mejor espaciado
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('Nombre:', 60, yCenter);
    pdf.setFont('helvetica', 'normal');
    pdf.text(student.name, 95, yCenter); // Ajustado posición X para el texto más grande
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('Curso:', 60, yCenter + lineSpacing);
    pdf.setFont('helvetica', 'normal');
    pdf.text(student.course, 95, yCenter + lineSpacing); // Ajustado posición X
    

    // Información de docentes
    const teacherInfo = await getTeacherInfo(student.course);
    if (teacherInfo) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Docente de aula:', 60, yCenter + 4 * lineSpacing);
      pdf.setFont('helvetica', 'normal');
      pdf.text(teacherInfo.spanishTeacher, 85, yCenter + 4 * lineSpacing);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Docente de inglés:', 60, yCenter + 5 * lineSpacing);
      pdf.setFont('helvetica', 'normal');
      pdf.text(teacherInfo.englishTeacher, 85, yCenter + 5 * lineSpacing);
    }

    // Nueva página para el gráfico
    if (chartImage) {
      pdf.addPage();
      addBackgroundAndSidebar();
      addHeader();

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Gráfico de Evaluación por Dimensiones', margin + 10, 50);

      // Calcular dimensiones del gráfico para ocupar la mayor parte de la página
      const maxWidth = pageWidth - margin - 30;
      const maxHeight = (pageHeight - 100) * 0.8;
      
      try {
        // Optimización: Detectar formato y aplicar compresión adecuada
        const imageFormat = chartImage.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
        const compressionLevel = imageFormat === 'JPEG' ? 'MEDIUM' : 'NONE';
        
        pdf.addImage(chartImage, imageFormat, margin + 10, 60, maxWidth, maxHeight, '', compressionLevel);
      } catch (error) {
        console.error('Error al agregar el gráfico:', error);
      }
    }

    // Página de descripción cualitativa
    if (student.qualitativeDescription && student.qualitativeDescription.trim()) {
      pdf.addPage();
      addBackgroundAndSidebar();
      addHeader();

      let currentY = margin + 15;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Descripción Cualitativa', margin + 10, currentY);
      currentY += 15;

      const maxWidth = 140;
      const lineHeight = 6;
      const textPadding = 5;
      
      const splitText = wrapText(student.qualitativeDescription, maxWidth);
      const leftMargin = margin + 10;
      
      let currentIndex = 0;
      while (currentIndex < splitText.length) {
        const remainingLines = splitText.length - currentIndex;
        const maxLinesPerContainer = Math.floor((pageHeight - currentY - 30) / lineHeight) - 2;
        const linesToShow = Math.min(remainingLines, maxLinesPerContainer);
        
        const textHeight = (linesToShow * lineHeight) + (2 * textPadding);
        const textWidth = maxWidth + (2 * textPadding);
        
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(leftMargin, currentY, textWidth, textHeight, cornerRadius, cornerRadius, 'S');
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        let y = currentY + textPadding + lineHeight;
        while (currentIndex < splitText.length && y + lineHeight <= currentY + textHeight - textPadding) {
          pdf.text(splitText[currentIndex], leftMargin + textPadding, y);
          y += lineHeight;
          currentIndex++;
        }

        if (currentIndex < splitText.length) {
          pdf.addPage();
          addBackgroundAndSidebar();
          addHeader();
          currentY = margin + 15;
        }
      }
    }

    // Generar nombre de archivo: RUT-informe_integral.pdf
    const rutLimpio = extractRutFromStudent(student);
    pdf.save(`${rutLimpio}-informe_integral.pdf`);
  }, [getTeacherInfo]);

  const exportPDFMassive = useCallback(async (students: Student[]) => {
    for (const student of students) {
      await exportToPDF(student);
    }
  }, [exportToPDF]);

  const handleExportToPDF = () => {
    const selectedStudent = findStudentById(students, selectedStudentIds[0]);
    if (selectedStudent) {
      exportToPDF(selectedStudent);
    } else {
      alert('Por favor, seleccione un estudiante antes de exportar a PDF.');
    }
  };

  // Añade este nuevo estado para el color de la sección seleccionada
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  // Modifica la función handleDimensionSelect
  const handleDimensionSelect = (dimensionId: number) => {
    setSelectedDimension(dimensionId);
    setActiveSection('formularios');
    const selectedCategory = dimensionCategories.find(category => 
      category.dimensions.some(dim => dim.id === dimensionId)
    );
    setSelectedColor(selectedCategory ? selectedCategory.color : null);
    setSelectedStudentIds([]); // Limpiar selección de estudiantes al cambiar de dimensión
  };

  const handleSectionChange = (section: 'dashboard' | 'formularios' | 'analytics' | 'usuarios' | 'exportacion' | 'configuracion' | 'retiro' | 'access-control' | 'contactos') => {
    // Proteger sección de usuarios/configuración - solo administradores
    if (section === 'usuarios' && role !== 'admin') {
      console.log('⚠️ Acceso denegado: Solo administradores pueden acceder a Configuración');
      setActiveSection('dashboard');
      return;
    }
    
    setActiveSection(section);

    // Refrescar datos de estudiantes cuando se ingresa a la sección de exportación
    if (section === 'exportacion') {
      refreshStudentsData();
    }

    // Notificar cambio de sección a los componentes de tutorial
    if (section === 'dashboard' || section === 'formularios' || section === 'analytics' || section === 'exportacion') {
      // Actualizar el estado del tutorial si está activo
      const tutorialState = localStorage.getItem('tutorialState');
      if (tutorialState) {
        try {
          const parsed = JSON.parse(tutorialState);
          if (parsed.isActive) {
            const updatedState = { ...parsed, currentSection: section };
            localStorage.setItem('tutorialState', JSON.stringify(updatedState));

            // Notificar a los componentes del cambio
            window.dispatchEvent(new CustomEvent('tutorialStateChanged', {
              detail: updatedState
            }));
          }
        } catch (error) {
          console.warn('Error actualizando estado del tutorial:', error);
        }
      }
    }
    
    // Controlar la visibilidad del sidebar según la sección y usuario
    if (section === 'formularios' && username !== SYSTEM_ADMIN_EMAIL) {
      setSidebarState('minimized'); // Solo en Formularios mostrar sidebar para usuarios normales
      setSelectedDimension(null); // Mostrar página de selección de dimensiones
    } else {
      setSidebarState('hidden'); // En todas las demás secciones o para admin, ocultar completamente
      setSelectedDimension(null); // Limpiar selección para otras secciones
    }
  };

  // Ajustar renderSelectedDimension para usar el nuevo tipo
  const renderSelectedDimension = useMemo(() => {
    return () => {
      const dimensionInfo = dimensionCategories
        .flatMap((cat) => cat.dimensions)
        .find((dim) => dim.id === selectedDimension);

      if (dimensionInfo) {
        const DimensionComponent = dimensionInfo.component as React.FC<any>;
        const studentsToPass = students;
        return (
          <DimensionComponent
            students={studentsToPass}
            selectedStudentIds={selectedStudentIds}
            handleLevelChange={handleLevelChange}
            handleNotEvaluatedChange={handleNotEvaluatedChange}
            viewMode={viewMode}
            handleViewModeChange={handleViewModeChange}
            showInstructions={showInstructions}
            handleDescriptionChange={handleDescriptionChange}
            exportToExcel={exportToExcel}
            exportToPDF={exportToPDF}
            exportPDFMassive={exportPDFMassive}
            selectedCourse={selectedCourse}
            setSelectedCourse={handleCourseSelect}
            courses={courses}
            handleStudentSelect={handleStudentSelect}
            getExpectedLevel={getExpectedLevel}
            getPreviousYearLevel={getPreviousYearLevel}
          />
        );
      }
      return null;
    };
  }, [dimensionCategories, selectedDimension, students, selectedStudentIds, handleLevelChange, handleNotEvaluatedChange, viewMode, showInstructions, handleDescriptionChange, exportToExcel, exportToPDF, exportPDFMassive, selectedCourse, handleCourseSelect, courses]);

  // Modificamos la función para calcular los iconos visibles
  const calculateVisibleIcons = () => {
    const sidebarHeight = window.innerHeight;
    const iconHeight = 60; // Altura estimada de cada icono + margen
    const logoHeight = 100; // Altura estimada del logo
    const topPadding = 60; // Espacio para el botón de toggle y otros elementos en la parte superior
    const bottomPadding = 20; // Espacio adicional en la parte inferior
    return Math.floor((sidebarHeight - logoHeight - topPadding - bottomPadding) / iconHeight);
  };

  const [visibleIcons, setVisibleIcons] = useState(calculateVisibleIcons());

  useEffect(() => {
    const handleResize = () => {
      setVisibleIcons(calculateVisibleIcons());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSelectedDimensionInfo = () => {
    switch (activeSection) {
      case 'dashboard':
        return { name: "Dashboard", icon: FaHome, category: "Vista general del sistema" };
      case 'analytics':
        return { name: "Analytics", icon: FaChartBar, category: "Análisis y visualización de datos" };
      case 'exportacion':
        return { name: "Exportación", icon: FaFileExcel, category: "Exportación de datos" };
      case 'usuarios':
        return { name: "Gestión de Usuarios", icon: FaUsers, category: "Administración del sistema" };
      case 'retiro':
        return { name: "Retiro", icon: FaGraduationCap, category: "Cálculo de notas y generación de informes" };
      case 'formularios':
        if (selectedDimension) {
    for (const category of dimensionCategories) {
      const selectedDim = category.dimensions.find(dim => dim.id === selectedDimension);
      if (selectedDim) {
        return {
          name: selectedDim.name,
          icon: selectedDim.icon,
          category: category.name
        };
      }
    }
        }
        return { name: "Formularios", icon: FaPen, category: "Selecciona una dimensión" };
      default:
        return { name: "Dashboard", icon: FaHome, category: "Vista general del sistema" };
    }
  };

  // Agregar estos estados cerca del inicio del componente
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState<number>(Date.now());
  const [updatesAvailable, setUpdatesAvailable] = useState<boolean>(false);
  const [checkingUpdates, setCheckingUpdates] = useState<boolean>(false);
  const updatesCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add this near the top of the file where other configuration constants are defined
  const DISABLE_AUTO_UPDATES = true; // Set to true to completely disable automatic update checking



  // Función para verificar actualizaciones
  const checkForUpdates = useCallback(async () => {
    if (!token || !authenticated || checkingUpdates || isLoading || DISABLE_AUTO_UPDATES) {
      return;
    }

    // Prevenir múltiples consultas simultáneas
    if (sessionStorage.getItem('lastCheckTimestamp')) {
      const lastCheck = parseInt(sessionStorage.getItem('lastCheckTimestamp') || '0');
      // Si ha pasado menos de 1 minuto desde la última consulta, no hacer nada
      if (Date.now() - lastCheck < 60000) {
        return;
      }
    }
    
    try {
      setCheckingUpdates(true);
      sessionStorage.setItem('lastCheckTimestamp', Date.now().toString());
      
      const courseFilter = selectedCourse || '';
      const response = await axios.get(`${API_BASE_URL}/api/check-updates`, {
        params: {
          lastSyncTimestamp,
          courseFilter
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.hasUpdates) {
        setUpdatesAvailable(true);
        
        // Si hay actualizaciones y no hay cambios locales sin guardar, cargar los nuevos datos
        if (!unsavedChanges) {
          await loadProgress(token);
          setLastSyncTimestamp(Date.now());
          setUpdatesAvailable(false);
        }
      }
    } catch (error) {
      console.error("Error al verificar actualizaciones:", error);
    } finally {
      setCheckingUpdates(false);
    }
  }, [token, authenticated, checkingUpdates, isLoading, DISABLE_AUTO_UPDATES, selectedCourse, API_BASE_URL, lastSyncTimestamp, unsavedChanges, loadProgress]);

  // Modify the update check interval configuration to respect this setting
  useEffect(() => {
    if (authenticated && token && !DISABLE_AUTO_UPDATES) {
      // Modificar el intervalo a 2 minutos en lugar de 30 segundos para reducir las consultas
      updatesCheckIntervalRef.current = setInterval(checkForUpdates, 120000);
      
      // Verificar al inicio
      checkForUpdates();
      
      return () => {
        if (updatesCheckIntervalRef.current) {
          clearInterval(updatesCheckIntervalRef.current);
        }
      };
    }
  }, [authenticated, token, checkForUpdates, DISABLE_AUTO_UPDATES]);





  // Añadir un efecto para advertir al usuario sobre cambios no guardados
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (unsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unsavedChanges]);

  // Estado removido - ahora se usa activeSection

  const handleDashboardDimensionSelect = (dimensionId: number) => {
    handleDimensionSelect(dimensionId);
  };

  // Funciones corregidas para cálculo de nivel esperado y año anterior
  // Nota: el parámetro representa la edad ancla A (por curso), no la edad real
  const getExpectedLevel = (anchoredAge: number): number => {
    // Índice en arreglo de niveles 4..12 → 0..8
    const targetAge = Math.min(12, anchoredAge + 1); // Esperado: A+1
    return Math.max(0, Math.min(8, targetAge - 4));
  };

  const getPreviousYearLevel = (anchoredAge: number): number => {
    // Anterior: A
    const targetAge = Math.max(3, Math.min(12, anchoredAge));
    return Math.max(0, Math.min(8, targetAge - 4));
  };

  // Nueva lógica de navegación principal
  const [activeSection, setActiveSection] = useState<'dashboard' | 'formularios' | 'analytics' | 'usuarios' | 'exportacion' | 'configuracion' | 'retiro' | 'access-control' | 'contactos'>('dashboard');

  // Inicializar funcionalidad global del tutorial
  useEffect(() => {
    if (authenticated && (role === 'admin' || role === 'teacher')) {
      // Crear función global para iniciar tutorial
      window.startTutorial = () => {
        console.log('🚀 Iniciando tutorial desde función global');
        const tutorialState = {
          isActive: true,
          currentStep: 0,
          currentSection: 'dashboard'
        };
        localStorage.setItem('tutorialState', JSON.stringify(tutorialState));
        
        // Navegar a dashboard si no estamos ahí
        if (activeSection !== 'dashboard') {
          handleSectionChange('dashboard');
        }
        
        // Disparar evento para que los componentes reaccionen
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('tutorialStateChanged', { 
            detail: tutorialState 
          }));
        }, 100);
      };

      // Mensaje de consola para usuarios que quieran iniciar el tutorial
      console.log('💡 Para iniciar el tutorial, escribe en la consola: startTutorial()');

      // Escuchar eventos de navegación del tutorial
      const handleTutorialNavigation = (event: CustomEvent) => {
        const { section } = event.detail;
        console.log('🧭 Recibiendo solicitud de navegación del tutorial:', section);
        handleSectionChange(section);
      };

      window.addEventListener('tutorialNavigationRequest', handleTutorialNavigation as EventListener);

      return () => {
        window.removeEventListener('tutorialNavigationRequest', handleTutorialNavigation as EventListener);
        delete window.startTutorial;
      };
    }
  }, [authenticated, role, activeSection]);

  // Modificar el renderizado para incluir el loading
  return (
    <TenantProvider>
      <SocketProvider>
        <div>
        {/* El componente ConnectionStatusMonitor está temporalmente desactivado 
         mientras se resuelven los problemas de TypeScript */}
        {/* Notificación */}
        {notification.show && (
          <div
            className={`fixed top-5 right-5 p-4 rounded-md shadow-lg z-50 transition-all duration-300 ease-in-out transform flex items-center justify-between ${
              notification.show ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
            } ${
              notification.type === 'success' 
                ? 'bg-green-100 text-green-800 border-l-4 border-green-500' 
                : notification.type === 'warning'
                  ? 'bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500'
                  : 'bg-red-100 text-red-800 border-l-4 border-red-500'
            }`}
            style={{
              animation: notification.show ? 'slideIn 0.3s ease-out forwards' : 'slideOut 0.3s ease-in forwards',
              minWidth: '300px',
              maxWidth: '500px'
            }}
          >
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <svg 
                  className="w-5 h-5 mr-2 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                    clipRule="evenodd"
                  />
                </svg>
              ) : notification.type === 'warning' ? (
                <svg 
                  className="w-5 h-5 mr-2 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg 
                  className="w-5 h-5 mr-2 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span className="flex-grow">{notification.message}</span>
            </div>
            <button
              onClick={() => setNotification(prev => ({ ...prev, show: false }))}
              className={`ml-4 p-1 rounded-full hover:bg-opacity-20 transition-colors duration-200 ${
                notification.type === 'success' 
                  ? 'hover:bg-green-500' 
                  : notification.type === 'warning'
                    ? 'hover:bg-yellow-500'
                    : 'hover:bg-red-500'
              }`}
              aria-label="Cerrar notificación"
            >
              <svg 
                className="w-4 h-4" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
        
        {isLoading ? (
          <div className="min-h-screen flex flex-col md:flex-row">
            {/* Left side - Loading state with background image */}
            <div className="w-full md:w-1/3 bg-gradient-to-b from-blue-900 to-blue-700 flex flex-col justify-center items-center p-8 md:p-16 relative overflow-hidden h-screen">
              <div className="absolute inset-0 z-0">
                <img src={schoolIllustration} alt="School Illustration" className="w-full h-full object-cover object-center opacity-20" style={{ objectPosition: 'center' }} />
              </div>
              <div className="max-w-md z-10 relative">
                <h2 className="text-xl font-bold mb-6 text-blue-100 text-center">Plataforma de ingreso y análisis de datos pedagógicos para el seguimiento del desarrollo estudiantil.</h2>
              </div>
            </div>
            
            {/* Right side - Loading indicator */}
            <div className="w-full md:w-2/3 flex justify-center items-center bg-white h-screen">
              <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          </div>
        ) : !authenticated ? (
          !showLogin ? (
            <LandingPage onLoginClick={() => setShowLogin(true)} />
          ) : (
            <div className="min-h-screen flex flex-col md:flex-row relative bg-slate-50 dark:bg-[#020617] transition-colors duration-500 overflow-hidden">
              {/* Mesh Background for Login */}
              <div className="absolute inset-0 z-0 opacity-10 dark:opacity-20 pointer-events-none transition-opacity duration-500">
                <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-300 dark:bg-indigo-600 rounded-full blur-[150px] transition-colors duration-500" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-300 dark:bg-purple-600 rounded-full blur-[150px] transition-colors duration-500" />
              </div>

              {/* Botón de Cambio de Tema (Sol/Luna) */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="absolute top-8 right-8 z-50 p-4 rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-white shadow-xl transition-all duration-500 backdrop-blur-xl group overflow-hidden"
                aria-label={isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              >
                <div className="relative w-6 h-6 flex items-center justify-center">
                  <FaSun 
                    className={`absolute transition-all duration-500 transform ${isDarkMode ? 'translate-y-12 opacity-0' : 'translate-y-0 opacity-100'}`} 
                    size={20} 
                  />
                  <FaMoon 
                    className={`absolute transition-all duration-500 transform ${isDarkMode ? 'translate-y-0 opacity-100' : '-translate-y-12 opacity-0'}`} 
                    size={20} 
                  />
                </div>
              </button>

              {/* Left side - Image only (no title) */}
              <div className="w-full md:w-1/3 relative hidden md:flex md:flex-col md:justify-center md:items-center p-12 overflow-hidden h-screen border-r border-slate-200 dark:border-white/5 transition-colors duration-500">
                <div className="absolute inset-0 z-0 opacity-10">
                  <img src={schoolIllustration} alt="School Illustration" className="w-full h-full object-cover object-center grayscale" />
                </div>
                <div className="relative z-10 text-slate-800 dark:text-white text-center transition-colors duration-500">
                  <div className="w-24 h-24 bg-indigo-100 dark:bg-indigo-600/20 rounded-[2.5rem] flex items-center justify-center mb-10 mx-auto backdrop-blur-xl border border-indigo-200 dark:border-indigo-500/30 shadow-2xl shadow-indigo-500/10 dark:shadow-indigo-500/20 transition-all duration-500">
                    <FaLock size={36} className="text-indigo-600 dark:text-indigo-400 transition-colors duration-500" />
                  </div>
                  <h2 className="text-4xl font-extrabold mb-6 font-editorial italic tracking-tight">Acceso Seguro</h2>
                  <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed max-w-xs mx-auto transition-colors duration-500">
                    Ingrese sus credenciales institucionales para gestionar el potencial educativo.
                  </p>
                </div>
              </div>
              
              {/* Right side - Login form */}
              <div className="w-full md:w-2/3 flex justify-center items-center p-6 md:p-12 relative z-10 transition-colors duration-500">
                <div className="w-full max-w-lg p-12 bg-white dark:bg-white/5 backdrop-blur-2xl rounded-[3rem] shadow-2xl dark:shadow-[0_0_100px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-white/10 transition-all duration-500">
                  {/* Modern App Title */}
                  <div className="text-center mb-12">
                    <div className="flex items-center justify-center h-32 mb-6 drop-shadow-[0_0_20px_rgba(99,102,241,0.3)]">
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/logos/logo-csn.png`}
                        alt="Logo Colegio Sin Notas" 
                        className="h-full w-auto object-contain brightness-110" 
                      />
                    </div>
                    <p className="text-xs text-slate-500 font-black uppercase tracking-[0.4em] transition-colors duration-500">Inteligencia Educativa</p>
                  </div>
                
                <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-10 text-center tracking-tight transition-colors duration-500">
                  {isRegistering ? "Nueva Cuenta" : "Iniciar Sesión"}
                </h2>
                
                {error && (
                  <div className="bg-rose-500/10 border border-rose-500/20 text-rose-400 p-4 rounded-2xl mb-8 text-sm font-bold">
                    {error}
                  </div>
                )}
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3 ml-1 transition-colors duration-500">
                      Correo Electrónico
                    </label>
                    <input
                      type="email"
                      autoComplete="email"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="p-4 bg-slate-50 dark:bg-white/5 border border-slate-300 dark:border-white/10 rounded-2xl w-full text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 duration-500"
                      placeholder="ejemplo@colegio.cl"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3 ml-1 transition-colors duration-500">
                      Contraseña Privada
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        autoComplete={isRegistering ? "new-password" : "current-password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="p-4 bg-slate-50 dark:bg-white/5 border border-slate-300 dark:border-white/10 rounded-2xl w-full text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 duration-500"
                        placeholder="••••••••"
                        required
                        minLength={8}
                      />
                      <button 
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-indigo-600 dark:text-slate-500 dark:hover:text-white transition-colors duration-500"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.522 3 10 3a9.995 9.995 0 00-4.542 1.076L3.707 2.293zM10 5c3.478 0 6.36 2.115 7.53 5-.438 1.078-1.145 2.008-2.043 2.757L13.35 10.62a3 3 0 00-4.03-4.03L7.18 4.45A9.917 9.995 0 0110 5z" clipRule="evenodd" />
                            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.995 0 00.458 10c1.274 4.057 5.02 7 9.542 7 .847 0 1.666-.105 2.454-.303z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {!isRegistering && (
                    <div className="flex justify-end">
                      <button 
                        className="text-indigo-600 dark:text-indigo-400 text-xs font-black uppercase tracking-widest hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors duration-500"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Recuperar Acceso
                      </button>
                    </div>
                  )}
                  
                  <button
                    onClick={() => isRegistering ? handleRegister() : handleLogin(username, password)}
                    className="w-full bg-indigo-600 text-white py-5 rounded-2xl hover:bg-indigo-500 transition-all duration-500 font-black tracking-widest uppercase shadow-2xl shadow-indigo-200 dark:shadow-indigo-900/40 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    disabled={isLoading || !username || !password}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-3"></div>
                        <span>AUTENTICANDO...</span>
                      </div>
                    ) : (
                      isRegistering ? "CREAR CUENTA INSTITUCIONAL" : "ENTRAR AL SISTEMA"
                    )}
                  </button>
                  
                  <div className="pt-6 text-center">
                    <button
                      onClick={toggleRegister}
                      className="text-slate-500 hover:text-indigo-600 dark:text-slate-500 dark:hover:text-indigo-400 focus:outline-none font-bold text-sm transition-colors duration-500"
                    >
                      {isRegistering ? "← VOLVER AL LOGIN" : "¿NO TIENE CUENTA? REGISTRAR INSTITUCIÓN"}
                    </button>
                  </div>
                </div>
                
                <div className="mt-12 pt-10 border-t border-slate-200 dark:border-white/5 text-center text-[10px] text-slate-500 dark:text-slate-600 font-black uppercase tracking-[0.3em] transition-colors duration-500">
                  <p>SISTEMA DE GESTIÓN DE HABILIDADES © {new Date().getFullYear()}</p>
                </div>
              </div>
            </div>
          </div>
          )
        ) : (
          // Tu contenido principal aquí
          <div className="min-h-screen bg-gray-100 flex">
            {/* Barra lateral Mejorada */}
            <div className={`bg-slate-900 text-slate-100 h-screen fixed left-0 top-0 flex flex-col transition-all duration-300 shadow-xl z-20
              ${sidebarState === 'hidden' ? 'w-0' : sidebarState === 'minimized' ? 'w-24' : 'w-72'}`}> {/* Increased w-20 to w-24 for minimized text */}
              {sidebarState !== 'hidden' && (
                <>
                  {/* User Section & Toggle Button */}
                  <div className={`p-4 flex ${sidebarState === 'minimized' ? 'justify-center' : 'justify-end'} items-center border-b border-slate-700`}> {/* Changed justify-between to justify-end for expanded */}
                    {/* User info removed from here */}
                    <button
                      onClick={toggleSidebar}
                      className="text-slate-300 p-2 rounded-lg hover:bg-slate-700 transition-colors"
                      title={sidebarState === 'minimized' ? "Expandir menú" : "Minimizar menú"}
                    >
                      {sidebarState === 'minimized' ? <FaChevronRight size={18} /> : <FaChevronLeft size={18} />}
                    </button>
                  </div>

                  {/* Navigation Links */}
                  <nav className="flex-grow overflow-y-auto py-4 space-y-1">
                    {/* Header for current section */}
                    {sidebarState === 'expanded' && (
                      <div className="px-2 mb-4">
                        <div className="flex items-center">
                          {(() => {
                            const { name, icon: Icon, category } = getSelectedDimensionInfo();
                            return (
                              <>
                                <div className="bg-slate-600 bg-opacity-50 p-2 rounded-full mr-3">
                                  <Icon className="text-lg text-white" />
                                </div>
                                <div className="flex flex-col">
                                  <h3 className="text-sm font-semibold text-white">{name}</h3>
                                  {category && <span className="text-xs text-slate-400">{category}</span>}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Separator for Formularios section */}
                    {activeSection === 'formularios' && sidebarState === 'expanded' && (
                      <div className="px-2 mb-2">
                        <span className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                          Dimensiones
                        </span>
                      </div>
                    )}

                    {/* Content based on active section */}
                    {activeSection === 'formularios' && (
                      <>
                        {/* Dimension Categories - Only show in Formularios section */}
                    {sidebarState === 'expanded' ? (
                      dimensionCategories.map((category) => {
                        // Filtrar dimensiones según permisos del usuario y tenant
                        const filteredDimensions = category.dimensions.filter(dimension => canViewDimension(dimension.id));

                        // Solo mostrar la categoría si tiene dimensiones filtradas
                        if (filteredDimensions.length === 0) return null;

                        return (
                          <div key={category.name} className="px-2">
                            <button
                              onClick={() => toggleCategory(category.name)}
                              className="flex items-center justify-between w-full px-2 py-2.5 text-xs font-semibold tracking-wide uppercase rounded-md text-slate-400 hover:text-slate-200 transition-colors"
                            >
                              <span>{category.name}</span>
                              <FaChevronRight
                                className={`transform transition-transform duration-200 ${openCategories.includes(category.name) ? 'rotate-90' : ''}`}
                                size={12}
                              />
                            </button>
                            {openCategories.includes(category.name) && (
                              <div className="mt-1 space-y-1 pl-2 border-l border-slate-700 ml-2">
                                {filteredDimensions.map((dim) => (
                                  <button
                                    key={dim.id}
                                    onClick={() => handleDimensionSelect(dim.id)}
                                    className={`flex items-center w-full px-3 py-2.5 text-sm rounded-md transition-colors duration-150
                                      ${selectedDimension === dim.id
                                        ? 'bg-indigo-500 text-white font-medium shadow-sm'
                                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                                      }`}
                                    title={dim.name}
                                  >
                                    <dim.icon size={18} className="mr-3" />
                                    <span>{dim.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : ( // Minimized state
                      dimensionCategories.flatMap(category => category.dimensions)
                        .filter(dimension => canViewDimension(dimension.id))
                        .map((dim) => (
                          <button
                            key={dim.id}
                            onClick={() => handleDimensionSelect(dim.id)}
                            className={`flex flex-col items-center justify-center text-center w-full py-2.5 px-1 transition-colors duration-200 rounded-md
                              ${selectedDimension === dim.id
                                ? 'bg-indigo-600 text-white'
                                : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                              }`}
                            title={dim.name}
                          >
                            <dim.icon size={22} className="mb-0.5" />
                            <span className="text-[10px] leading-tight break-words w-full px-0.5">{dim.name}</span>
                          </button>
                        ))
                        )}
                      </>
                    )}

                    {/* Content for Dashboard section */}
                    {activeSection === 'dashboard' && sidebarState === 'expanded' && (
                      <div className="space-y-2">
                        <div className="px-2 mb-2">
                          <span className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                            Accesos Rápidos
                          </span>
                        </div>
                        {dimensionCategories.slice(0, 3).map((category) => {
                          // Filtrar dimensiones según permisos del usuario
                          const filteredDimensions = category.dimensions
                            .filter(dimension => canViewDimension(dimension.id))
                            .slice(0, 4);

                          // Solo mostrar la categoría si tiene dimensiones filtradas
                          if (filteredDimensions.length === 0) return null;

                          return (
                            <div key={category.name} className="px-2">
                              <div className="bg-slate-700 bg-opacity-30 rounded-lg p-3 mb-2">
                                <h4 className="text-xs font-medium text-slate-300 mb-2">{category.name}</h4>
                                <div className="grid grid-cols-2 gap-1">
                                  {filteredDimensions.map((dim) => (
                                    <button
                                      key={dim.id}
                                      onClick={() => handleDimensionSelect(dim.id)}
                                      className="flex flex-col items-center p-2 rounded hover:bg-slate-600 transition-colors"
                                      title={dim.name}
                                    >
                                      <dim.icon size={16} className="text-slate-400 mb-1" />
                                      <span className="text-[10px] text-slate-400 text-center leading-tight">{dim.name.split(' ')[0]}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Content for Analytics section */}
                    {activeSection === 'analytics' && sidebarState === 'expanded' && (
                      <div className="space-y-2">
                        <div className="px-2 mb-2">
                          <span className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                            Herramientas de Análisis
                          </span>
                        </div>
                        <div className="px-2 space-y-1">
                          <div className="bg-slate-700 bg-opacity-30 rounded-lg p-3">
                            <div className="flex items-center mb-2">
                              <FaChartBar className="text-blue-400 mr-2" size={14} />
                              <span className="text-sm text-slate-300">Visualización</span>
                            </div>
                            <p className="text-xs text-slate-400">Gráficos y reportes de progreso estudiantil</p>
                          </div>
                          <div className="bg-slate-700 bg-opacity-30 rounded-lg p-3">
                            <div className="flex items-center mb-2">
                              <FaFileExcel className="text-green-400 mr-2" size={14} />
                              <span className="text-sm text-slate-300">Exportación</span>
                            </div>
                            <p className="text-xs text-slate-400">Descarga datos en Excel y PDF</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Content for Users section - Solo visible para administradores */}
                    {activeSection === 'usuarios' && sidebarState === 'expanded' && role === 'admin' && (
                      <div className="space-y-2">
                        <div className="px-2 mb-2">
                          <span className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                            Administración
                          </span>
                        </div>
                        <div className="px-2 space-y-1">
                          <div className="bg-slate-700 bg-opacity-30 rounded-lg p-3">
                            <div className="flex items-center mb-2">
                              <FaUserShield className="text-purple-400 mr-2" size={14} />
                              <span className="text-sm text-slate-300">Roles y Permisos</span>
                            </div>
                            <p className="text-xs text-slate-400">Gestión de administradores y profesores</p>
                          </div>
                          <div className="bg-slate-700 bg-opacity-30 rounded-lg p-3">
                            <div className="flex items-center mb-2">
                              <FaCog className="text-orange-400 mr-2" size={14} />
                              <span className="text-sm text-slate-300">Configuración</span>
                            </div>
                            <p className="text-xs text-slate-400">Ajustes del sistema y cursos</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </nav>

                  {/* Footer: App Title */}
                  <div className="p-4 border-t border-slate-700 mt-auto"> {/* Added mt-auto to push to bottom */}
                     {sidebarState === 'expanded' && (
                         <div className="text-center">
                           <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl mb-2 shadow-lg">
                             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                             </svg>
                           </div>
                           <p className="text-xs font-medium text-slate-300">Colegio Sin Notas</p>
                         </div>
                     )}
                     {sidebarState === 'minimized' && (
                         <div className="flex justify-center">
                           <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-lg">
                             <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                             </svg>
                           </div>
                         </div>
                     )}
                    {/* Logout button removed from here */}
                  </div>
                </>
              )}
            </div>

            {/* Menú desplegable para iconos adicionales - REVISIT IF NEEDED FOR MINIMIZED STATE, CURRENTLY ALL SHOWN */}
            {showMoreMenu && sidebarState === 'minimized' && (
              <div className="fixed left-24 top-0 bg-gray-600 p-2 rounded-r-lg shadow-lg z-50">
                <div className="grid grid-cols-2 gap-2">
                  {dimensionCategories.flatMap(category => category.dimensions)
                    .filter(dimension => canViewDimension(dimension.id))
                    .slice(visibleIcons).map((dim: {id: number, name: string, icon: IconType}) => (
                      <button
                        key={dim.id}
                        onClick={() => {
                          handleDimensionSelect(dim.id);
                          setShowMoreMenu(false);
                        }}
                        className="flex flex-col items-center p-2 hover:bg-gray-500 rounded"
                      >
                        <dim.icon className="text-xl mb-1" />
                        <span className="text-[8px] text-center">{dim.name}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Contenido principal */}
            <div className={`transition-all duration-300 ${sidebarState === 'hidden' ? 'ml-0' : sidebarState === 'minimized' ? 'ml-24' : 'ml-72'} w-full bg-slate-50 dark:bg-[#020617] min-h-screen transition-colors duration-500`}> {/* Adjusted minimized margin back to ml-24 */}
              {/* Barra superior fija */}
              <nav className={`bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-900 dark:to-slate-800 text-white fixed top-0 right-0 h-16
                ${sidebarState === 'hidden' ? 'left-0' : sidebarState === 'minimized' ? 'left-24' : 'left-72'} 
                z-30 transition-all duration-500 shadow-lg`}> {/* Increased z-index to 30 to be above sidebar (z-20) */}
                
                {/* Contenedor principal dividido en tres secciones */}
                <div className="flex items-stretch justify-between w-full h-full">
                  {/* Sección izquierda: Control de sidebar */}
                  <div className="flex items-center py-0 px-4 h-full min-w-0">
                    {sidebarState === 'hidden' && (
                      <button
                        onClick={toggleSidebar}
                        className="text-slate-300 p-2 rounded-lg hover:bg-slate-700 transition-colors"
                        title="Mostrar menú"
                      >
                        <FaChevronRight size={18}/>
                      </button>
                    )}
                  </div>
                  
                  {/* Sección central: Navegación principal */}
                  <div className="flex-grow flex items-center justify-center px-4 h-full">
                    <div className="navigation-menu flex items-center space-x-1 bg-slate-700 bg-opacity-50 rounded-lg p-1">
                      {/* Navegación para Admin del Sistema */}
                      {username === SYSTEM_ADMIN_EMAIL ? (
                        <>
                          {/* Resumen */}
                          <button
                            onClick={() => handleSectionChange('dashboard')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'dashboard' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaChartLine size={16} />
                            <span className="text-sm font-medium">Resumen</span>
                          </button>

                          {/* Organizaciones */}
                          <button
                            onClick={() => handleSectionChange('formularios')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'formularios' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaBuilding size={16} />
                            <span className="text-sm font-medium">Organizaciones</span>
                          </button>

                          {/* Gestión de Usuarios */}
                          <button
                            onClick={() => handleSectionChange('usuarios')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'usuarios' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaUsers size={16} />
                            <span className="text-sm font-medium">Gestión de Usuarios</span>
                          </button>

                          {/* Control de Acceso */}
                          <button
                            onClick={() => handleSectionChange('access-control')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'access-control' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaUserShield size={16} />
                            <span className="text-sm font-medium">Control de Acceso</span>
                          </button>

                          {/* Datos e Importación */}
                          <button 
                            onClick={() => handleSectionChange('analytics')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'analytics' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaSearch size={16} />
                            <span className="text-sm font-medium">Datos e Importación</span>
                          </button>

                          {/* Solicitudes Contacto */}
                          <button 
                            onClick={() => handleSectionChange('contactos')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'contactos' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaEnvelope size={16} />
                            <span className="text-sm font-medium">Solicitudes Contacto</span>
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Dashboard para usuarios normales */}
                          <button
                            onClick={() => handleSectionChange('dashboard')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'dashboard' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaHome size={16} />
                            <span className="text-sm font-medium">Dashboard</span>
                          </button>

                          {/* Formularios */}
                          <button
                            data-section="formularios"
                            onClick={() => handleSectionChange('formularios')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'formularios' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaPen size={16} />
                            <span className="text-sm font-medium">Formularios</span>
                          </button>

                          {/* Analytics */}
                          <button 
                            data-section="analytics"
                            onClick={() => handleSectionChange('analytics')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'analytics' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaChartBar size={16} />
                            <span className="text-sm font-medium">Analytics</span>
                          </button>

                          {/* Exportación */}
                          <button 
                            data-section="exportacion"
                            onClick={() => handleSectionChange('exportacion')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                              activeSection === 'exportacion' 
                                ? 'bg-white text-slate-800 shadow-md font-medium' 
                                : 'text-slate-300 hover:text-white hover:bg-slate-600'
                            }`}
                          >
                            <FaFileExcel size={16} />
                            <span className="text-sm font-medium">Exportación</span>
                          </button>

                          {/* Usuarios/Configuración - Solo visible para administradores */}
                          {role === 'admin' && (
                            <button
                              onClick={() => handleSectionChange('usuarios')}
                              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                                activeSection === 'usuarios' 
                                  ? 'bg-white text-slate-800 shadow-md font-medium' 
                                  : 'text-slate-300 hover:text-white hover:bg-slate-600'
                              }`}
                            >
                              <FaUsers size={16} />
                              <span className="text-sm font-medium">Configuración</span>
                            </button>
                          )}

                        </>
                      )}
                      </div>
                    </div>
                    
                  {/* Sección derecha: Botones de funcionalidad */}
                  <div className="flex items-center px-4 h-full min-w-0 space-x-3">
                    {/* Mostrar si hay cambios sin guardar */}
                    {unsavedChanges && (
                      <div className="bg-yellow-500 bg-opacity-20 text-yellow-200 px-3 py-1.5 rounded-full text-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                        <span>Cambios sin guardar</span>
                    </div>
                    )}
                  
                    {/* Botón de actualizaciones disponibles */}
                    {updatesAvailable && (
                      <button 
                        onClick={async () => {
                          if (unsavedChanges) {
                            if (window.confirm('Hay cambios sin guardar. ¿Desea descartar los cambios y cargar las actualizaciones?')) {
                              await loadProgress(token as string);
                            }
                          } else {
                            await loadProgress(token as string);
                          }
                        }} 
                        className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 shadow-sm"
                        title="Hay actualizaciones de otros usuarios"
                      >
                        <svg className="w-4 h-4 mr-1 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Actualizar</span>
                      </button>
                    )}
                    
                    {/* 🧪 BOTONES DE TESTING */}
                    {/* Botón de guardar */}
                    <button 
                      onClick={saveProgress} 
                      className={`relative flex items-center justify-center text-sm font-medium shadow-md transition-all duration-200 transform hover:-translate-y-px
                        ${isSaving 
                          ? 'bg-yellow-500 hover:bg-yellow-600 cursor-not-allowed' 
                          : unsavedChanges 
                            ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                        } px-4 py-2 rounded-lg`}
                      disabled={isSaving}
                      title={isSaving ? "Guardando cambios..." : (unsavedChanges ? "Guardar cambios pendientes" : "Guardar datos")}
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Guardando...</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="text-md mr-2" /> 
                          <span>{unsavedChanges ? 'Guardar' : 'Guardar'}</span>
                        </>
                      )}
                      
                      {/* Indicador de cambios sin guardar */}
                      {unsavedChanges && !isSaving && (
                        <span className="absolute top-0 right-0 transform translate-x-1/3 -translate-y-1/3 flex h-3.5 w-3.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-pink-500 border-2 border-white"></span>
                        </span>
                      )}
                    </button>
                    
                    {/* Botón de ayuda */}
                    <button 
                      onClick={() => {
                        setShowHelpModal(true);
                        setActiveHelpTab('instructions');
                      }}
                      className="bg-slate-600 hover:bg-slate-500 p-2.5 rounded-lg text-slate-200 hover:text-white transition-colors duration-150 flex items-center shadow-sm"
                      title="Ayuda e Información"
                    >
                      <FaQuestionCircle className="text-lg" />
                    </button>

                    {/* Botón de Cambio de Tema (Sol/Luna) - En Navbar Logueado */}
                    <button
                      onClick={() => setIsDarkMode(!isDarkMode)}
                      className="bg-slate-600 hover:bg-slate-500 p-2.5 rounded-lg text-slate-200 hover:text-white transition-all duration-300 flex items-center shadow-sm group overflow-hidden"
                      title={isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
                    >
                      <div className="relative w-5 h-5 flex items-center justify-center">
                        <FaSun 
                          className={`absolute transition-all duration-500 transform ${isDarkMode ? 'translate-y-10 opacity-0' : 'translate-y-0 opacity-100'}`} 
                          size={16} 
                        />
                        <FaMoon 
                          className={`absolute transition-all duration-500 transform ${isDarkMode ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`} 
                          size={16} 
                        />
                      </div>
                    </button>
                    
                    {/* Avatar de usuario con dropdown */}
                    <div className="relative user-dropdown">
                      <button
                        onClick={() => setShowUserDropdown(!showUserDropdown)}
                        className="flex items-center space-x-2 bg-slate-600 bg-opacity-50 hover:bg-slate-600 px-3 py-2 rounded-lg transition-colors duration-150 border border-slate-500"
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {username.charAt(0).toUpperCase()}
                        </div>
                        <div className="hidden md:flex flex-col items-start">
                          <span className="text-sm font-medium text-white truncate max-w-[100px]">{username}</span>
                          <span className="text-xs text-slate-300 capitalize">{role}</span>
                        </div>
                        <FaChevronDown className={`text-slate-300 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} size={12} />
                      </button>

                      {/* Dropdown del usuario */}
                      {showUserDropdown && (
                        <div className="absolute top-full right-0 mt-2 w-64 bg-slate-800 text-white rounded-lg shadow-2xl border border-slate-700 z-50">
                          <div className="p-4 border-b border-slate-700">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                {username.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-white">{username}</p>
                                <p className="text-sm text-slate-300 capitalize">{role}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-2">
                            <button
                              onClick={() => {
                                setShowChangePassword(true);
                                setShowUserDropdown(false);
                              }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-slate-700 rounded-md transition-colors duration-150 text-slate-200"
                            >
                              <span aria-hidden="true">🔑</span>
                              <span>Cambiar contraseña</span>
                            </button>
                    <button
                      onClick={() => {
                        if (unsavedChanges) {
                          if (window.confirm('Hay cambios sin guardar. ¿Está seguro de que desea salir?')) {
                            handleLogout();
                          }
                        } else {
                          handleLogout();
                        }
                                setShowUserDropdown(false);
                      }}
                              className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-slate-700 rounded-md transition-colors duration-150 text-red-300 hover:text-red-200"
                    >
                              <FaSignOutAlt />
                              <span>Cerrar Sesión</span>
                    </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </nav>

              {/* Contenido principal */}
              <div className="container mx-auto p-4 mt-20">
                {/* Renderizar AdminDashboard para admin del sistema en todas las secciones principales */}
                {username === SYSTEM_ADMIN_EMAIL && role && (activeSection === 'dashboard' || activeSection === 'formularios' || activeSection === 'analytics' || activeSection === 'contactos' || activeSection === 'usuarios' || activeSection === 'access-control') && (() => {
                  // Mapear secciones de App.tsx a pestañas de AdminDashboard
                  const getAdminTab = (section: string): 'overview' | 'organizations' | 'users' | 'data' | 'access-control' | 'contacts' => {
                    switch (section) {
                      case 'dashboard': return 'overview';
                      case 'formularios': return 'organizations';
                      case 'usuarios': return 'users';
                      case 'access-control': return 'access-control';
                      case 'analytics': return 'data';
                      case 'contactos': return 'contacts';
                      default: return 'overview';
                    }
                  };

                  return (
                    <AdminDashboard 
                      userRole={role}
                      userId={username}
                      initialTab={getAdminTab(activeSection)}
                    />
                  );
                })()}

                {/* Dashboard normal para usuarios regulares */}
                {activeSection === 'dashboard' && role && username !== SYSTEM_ADMIN_EMAIL && (
                  <SimpleTutorialSection 
                    section="dashboard"
                    onDimensionSelect={handleDimensionSelect}
                    onViewModeChange={handleViewModeChange}
                  >
                    <Dashboard
                      username={username}
                      role={role}
                      allowedCourses={allowedCourses}
                      students={allStudents}
                      dimensionCategories={dimensionCategories}
                      dimensionsTemplate={dimensionsTemplate}
                      onDimensionSelect={handleDashboardDimensionSelect}
                      onSectionChange={handleSectionChange}
                      onViewModeChange={handleViewModeChange}
                    />
                  </SimpleTutorialSection>
                )} 
                
                {activeSection === 'analytics' && role && (role === 'admin' || role === 'teacher') && username !== SYSTEM_ADMIN_EMAIL && (
                  <SimplifiedAnalytics
                    students={allStudents}
                    dimensionsTemplate={dimensionsTemplate}
                    courses={courses}
                    role={role}
                  />
                )}
                
                {activeSection === 'exportacion' && role && (role === 'admin' || role === 'teacher') && username !== SYSTEM_ADMIN_EMAIL && (
                  <VisualizacionResultados
                    students={allStudents}
                    exportToExcel={exportToExcel}
                    exportToPDF={exportToPDF}
                    exportPDFMassive={exportPDFMassive}
                    onSectionChange={handleSectionChange}
                    onDimensionSelect={handleDimensionSelect}
                    onViewModeChange={handleViewModeChange}
                  />
                )}
                
                {activeSection === 'formularios' && selectedDimension && username !== SYSTEM_ADMIN_EMAIL && (
                  <FormulariosWrapper 
                    onSectionChange={handleSectionChange}
                    onDimensionSelect={handleDimensionSelect}
                    onViewModeChange={handleViewModeChange}
                  >
                    {renderSelectedDimension()}
                  </FormulariosWrapper>
                )}
                
                {activeSection === 'formularios' && !selectedDimension && username !== SYSTEM_ADMIN_EMAIL && (
                  <FormulariosWrapper 
                    onSectionChange={handleSectionChange}
                    onDimensionSelect={handleDimensionSelect}
                    onViewModeChange={handleViewModeChange}
                  >
                    <div className="p-6">
                    <div className="max-w-7xl mx-auto">
                      {/* Header */}
                      <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Formularios de Evaluación</h1>
                        <p className="text-lg text-gray-600">Selecciona una dimensión para comenzar a evaluar a los estudiantes</p>
              </div>

                      {/* Grid de categorías y dimensiones */}
                      <div className="dimension-categories space-y-8">
                        {dimensionCategories.map((category) => {
                          // Filtrar dimensiones según permisos del usuario
                          const filteredDimensions = category.dimensions.filter(dimension => canViewDimension(dimension.id));

                          // Si no hay dimensiones permitidas en esta categoría, no mostrar la categoría
                          if (filteredDimensions.length === 0) return null;

                          return (
                            <div key={category.name} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                              {/* Header de categoría */}
                              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: `${category.color}15` }}>
                                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                                  <div 
                                    className="w-4 h-4 rounded-full mr-3" 
                                    style={{ backgroundColor: category.color }}
                                  ></div>
                                  {category.name}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                  {filteredDimensions.length} dimensión{filteredDimensions.length !== 1 ? 'es' : ''} disponible{filteredDimensions.length !== 1 ? 's' : ''}
                                  {role === 'teacher' && allowedDimensions.length > 0 && (
                                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                      Asignadas
                                    </span>
                                  )}
                                </p>
                              </div>

                              {/* Grid de dimensiones */}
                              <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {filteredDimensions.map((dimension) => (
                                  <button
                                    key={dimension.id}
                                    onClick={() => handleDimensionSelect(dimension.id)}
                                    className="dimension-card group relative bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-indigo-500 hover:shadow-lg transition-all duration-200 text-left"
                                  >
                                    {/* Icono */}
                                    <div className="flex items-center mb-4">
                                      <div className="flex-shrink-0">
                                        <dimension.icon 
                                          size={32} 
                                          className="text-gray-600 group-hover:text-indigo-600 transition-colors duration-200" 
                                        />
                                      </div>
                                    </div>

                                    {/* Título */}
                                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-900 transition-colors duration-200 mb-2">
                                      {dimension.name}
                                    </h3>

                                    {/* Descripción o info adicional */}
                                    <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200">
                                      Evaluar habilidades de {dimension.name.toLowerCase()}
                                    </p>

                                    {/* Indicador visual */}
                                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                      <FaChevronRight className="text-indigo-500" size={16} />
                                    </div>

                                    {/* Borde decorativo */}
                                    <div 
                                      className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                      style={{ 
                                        backgroundImage: `linear-gradient(to right, ${category.color}, ${category.color}80)` 
                                      }}
                                    ></div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>

                      {/* Footer informativo */}
                      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-start">
                          <FaInfoCircle className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
                          <div>
                            <h3 className="text-lg font-medium text-blue-900 mb-2">Instrucciones de Uso</h3>
                            <ul className="text-sm text-blue-800 space-y-1">
                              <li>• Selecciona una dimensión para acceder al formulario de evaluación</li>
                              <li>• Puedes navegar entre dimensiones usando la barra lateral</li>
                              <li>• Los cambios se guardan automáticamente</li>
                              <li>• Usa los filtros por curso para evaluar grupos específicos</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </FormulariosWrapper>
                )}
                
                {/* UserManagement para administradores de organización (no super admin) */}
                {activeSection === 'usuarios' && username !== SYSTEM_ADMIN_EMAIL && role === 'admin' && (
                  <UserManagement 
                      courses={courses}
                      students={allStudents}
                      dimensions={dimensionsTemplate}
                      username={username}
                      onBackupComplete={() => {
                        console.log("Backup completado");
                      }}
                      onDimensionsUpdated={(newDimensions) => {
                        // TODO: Implementar actualización de dimensiones si es necesario
                        console.log("Dimensiones actualizadas:", newDimensions);
                      }}
                    />
                )}

              </div>
            </div>
          </div>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {forgotPasswordSuccess ? "Solicitud enviada" : "Recuperar acceso"}
                </h3>
                <button
                  onClick={closeForgotPasswordModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                  </svg>
                </button>
              </div>
              
              {forgotPasswordSuccess ? (
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700 mb-6">Avisamos al administrador de tu colegio. Te entregará una clave temporal; después puedes cambiarla en el menú de tu usuario → "Cambiar contraseña".</p>
                  <button
                    onClick={closeForgotPasswordModal}
                    className="w-full bg-gradient-to-r from-blue-700 to-blue-500 text-white py-3 rounded-lg hover:from-blue-800 hover:to-blue-600 transition duration-300 font-medium"
                  >
                    Entendido
                  </button>
                </div>
              ) : (
                <div>
                  {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
                      {error}
                    </div>
                  )}
                  <p className="text-gray-700 mb-4">Ingresa tu correo institucional. El administrador de tu colegio verá tu solicitud y te entregará una clave temporal.</p>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correo electrónico
                    </label>
                    <input
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="p-3 border border-gray-300 rounded-lg w-full focus:ring-[#1a56db] focus:border-[#1a56db] outline-none"
                      placeholder="Ingresa tu correo electrónico"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeForgotPasswordModal}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-300 font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleForgotPassword}
                      className="px-6 py-3 bg-gradient-to-r from-blue-700 to-blue-500 text-white rounded-lg hover:from-blue-800 hover:to-blue-600 transition duration-300 font-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                          Enviando...
                        </div>
                      ) : (
                        "Enviar solicitud"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cambiar contraseña propia (menú de usuario) */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                {resetDone ? "Contraseña actualizada" : "Cambiar contraseña"}
              </h3>
              {resetMessage && (
                <div className={`${resetDone ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"} p-3 rounded-md mb-4`}>
                  {resetMessage}
                </div>
              )}
              {resetDone ? (
                <button
                  onClick={closeResetModal}
                  className="w-full bg-gradient-to-r from-blue-700 to-blue-500 text-white py-3 rounded-lg hover:from-blue-800 hover:to-blue-600 transition duration-300 font-medium"
                >
                  Listo
                </button>
              ) : (
                <form onSubmit={handleChangeOwnPassword}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña actual (o la clave temporal que te entregaron)</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={resetCurrentPassword}
                    onChange={(e) => setResetCurrentPassword(e.target.value)}
                    className="p-3 border border-gray-300 rounded-lg w-full mb-4 focus:ring-[#1a56db] focus:border-[#1a56db] outline-none"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nueva contraseña (mínimo 6 caracteres)</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    className="p-3 border border-gray-300 rounded-lg w-full mb-4 focus:ring-[#1a56db] focus:border-[#1a56db] outline-none"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-2">Repetir contraseña</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    className="p-3 border border-gray-300 rounded-lg w-full mb-6 focus:ring-[#1a56db] focus:border-[#1a56db] outline-none"
                  />
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeResetModal}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-300 font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="px-6 py-3 bg-gradient-to-r from-blue-700 to-blue-500 text-white rounded-lg hover:from-blue-800 hover:to-blue-600 transition duration-300 font-medium"
                    >
                      {changingPassword ? "Guardando..." : "Guardar contraseña"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Modal combinado reemplaza los modales individuales */}

        {/* Modal combinado de ayuda e información */}
        {showHelpModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 transition-opacity duration-300 ease-in-out">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-0 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-in-out scale-95 animate-in">
              {/* Header del modal con tabs */}
              <div className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-t-xl border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between p-5">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
                    {activeHelpTab === 'instructions' ? (
                      <>
                        <FaQuestionCircle className="text-blue-500 dark:text-blue-400 mr-2" /> 
                        <span>Instrucciones de Uso</span>
                      </>
                    ) : activeHelpTab === 'about' ? (
                      <>
                        <FaInfoCircle className="text-indigo-500 dark:text-indigo-400 mr-2" /> 
                        <span>Información de la Aplicación</span>
                      </>
                    ) : activeHelpTab === 'status' ? (
                      <>
                        <FaWifi className="text-green-500 dark:text-green-400 mr-2" /> 
                        <span>Estado del Sistema</span>
                      </>
                    ) : (
                      <>
                        <FaBell className="text-amber-500 dark:text-amber-400 mr-2" /> 
                        <span>Novedades y Actualizaciones</span>
                      </>
                    )}
                  </h2>
                  <button 
                    onClick={() => setShowHelpModal(false)}
                    className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900"
                    aria-label="Cerrar modal"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
                
                {/* Navegación por tabs mejorada */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 px-5 gap-1">
                  <button
                    onClick={() => setActiveHelpTab('instructions')}
                    className={`py-3 px-5 font-medium text-sm rounded-t-lg transition-all duration-200 flex items-center ${
                      activeHelpTab === 'instructions' 
                        ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 border-t border-l border-r border-gray-200 dark:border-gray-700 shadow-sm' 
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:bg-opacity-50'
                    }`}
                  >
                    <FaQuestionCircle className="mr-2" /> Instrucciones
                  </button>
                  <button
                    onClick={() => setActiveHelpTab('about')}
                    className={`py-3 px-5 font-medium text-sm rounded-t-lg transition-all duration-200 flex items-center ${
                      activeHelpTab === 'about' 
                        ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 border-t border-l border-r border-gray-200 dark:border-gray-700 shadow-sm' 
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:bg-opacity-50'
                    }`}
                  >
                    <FaInfoCircle className="mr-2" /> Acerca de
                  </button>
                  <button
                    onClick={() => setActiveHelpTab('status')}
                    className={`py-3 px-5 font-medium text-sm rounded-t-lg transition-all duration-200 flex items-center ${
                      activeHelpTab === 'status' 
                        ? 'bg-white dark:bg-gray-900 text-green-600 dark:text-green-400 border-t border-l border-r border-gray-200 dark:border-gray-700 shadow-sm' 
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:bg-opacity-50'
                    }`}
                  >
                    <FaWifi className="mr-2" /> Estado
                  </button>
                  <button
                    onClick={() => setActiveHelpTab('updates')}
                    className={`py-3 px-5 font-medium text-sm rounded-t-lg transition-all duration-200 flex items-center ${
                      activeHelpTab === 'updates' 
                        ? 'bg-white dark:bg-gray-900 text-amber-600 dark:text-amber-400 border-t border-l border-r border-gray-200 dark:border-gray-700 shadow-sm' 
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:bg-opacity-50'
                    }`}
                  >
                    <FaBell className="mr-2" /> Novedades
                    {activeHelpTab !== 'updates' && (
                      <span className="flex h-2 w-2 relative ml-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Contenido del modal */}
              <div className="p-6 overflow-y-auto flex-grow scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {activeHelpTab === 'instructions' ? (
                  // Contenido de instrucciones mejorado
                  <div className="space-y-6">
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      Bienvenido al Sistema de Seguimiento de Habilidades Estudiantiles. A continuación se detallan las principales funcionalidades:
                    </p>
                    
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-5 border border-blue-100 dark:border-blue-800">
                      <div className="flex items-start">
                        <div className="bg-blue-100 dark:bg-blue-800 p-3 rounded-full mr-4">
                          <FaLightbulb className="text-blue-600 dark:text-blue-300 text-xl" />
                        </div>
                    <div>
                          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">Navegación</h3>
                          <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                        <li><strong>Barra lateral:</strong> Permite navegar entre las diferentes dimensiones de habilidades.</li>
                        <li><strong>Barra superior:</strong> Contiene las acciones principales como guardar, exportar y cerrar sesión.</li>
                            <li><strong>Dashboard:</strong> Visualiza estadísticas y resúmenes de progreso.</li>
                      </ul>
                    </div>
                      </div>
                    </div>
                    
                    <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-5 border border-indigo-100 dark:border-indigo-800">
                      <div className="flex items-start">
                        <div className="bg-indigo-100 dark:bg-indigo-800 p-3 rounded-full mr-4">
                          <FaUsers className="text-indigo-600 dark:text-indigo-300 text-xl" />
                    </div>
                    <div>
                          <h3 className="text-lg font-semibold text-indigo-700 dark:text-indigo-300 mb-2">Gestión de estudiantes</h3>
                          <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                        <li><strong>Selección:</strong> Puede seleccionar uno o varios estudiantes haciendo clic en sus tarjetas.</li>
                        <li><strong>Vista por estudiante:</strong> Muestra los niveles de habilidad de un estudiante específico.</li>
                        <li><strong>Vista por habilidad:</strong> Compara el mismo nivel de habilidad entre diferentes estudiantes.</li>
                      </ul>
                    </div>
                    </div>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-5 border border-green-100 dark:border-green-800">
                      <div className="flex items-start">
                        <div className="bg-green-100 dark:bg-green-800 p-3 rounded-full mr-4">
                          <FaSave className="text-green-600 dark:text-green-300 text-xl" />
                    </div>
                    <div>
                          <h3 className="text-lg font-semibold text-green-700 dark:text-green-300 mb-2">Datos y evaluaciones</h3>
                          <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                            <li><strong>Guardado automático:</strong> Los cambios se guardan automáticamente al seleccionar un nivel.</li>
                            <li><strong>Exportación:</strong> Puede exportar los datos en formato Excel y PDF.</li>
                            <li><strong>Filtrado:</strong> Puede filtrar por curso y habilidad para facilitar la evaluación.</li>
                      </ul>
                    </div>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-5 border border-purple-100 dark:border-purple-800">
                      <div className="flex items-start">
                        <div className="bg-purple-100 dark:bg-purple-800 p-3 rounded-full mr-4">
                          <FaCog className="text-purple-600 dark:text-purple-300 text-xl" />
                    </div>
                    <div>
                          <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-300 mb-2">Funcionalidades avanzadas</h3>
                          <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                            <li><strong>Reportes gráficos:</strong> Visualice el progreso a través de gráficos interactivos.</li>
                            <li><strong>Actualización anual:</strong> Herramienta para promocionar estudiantes al siguiente nivel académico.</li>
                            <li><strong>Generación de informes:</strong> Cree informes detallados para compartir con padres y directivos.</li>
                      </ul>
                    </div>
                  </div>
                    </div>
                  </div>
                ) : activeHelpTab === 'about' ? (
                  // Contenido de acerca de mejorado
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <img src={logoImage} alt="Logo" className="w-32 mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Sistema de Seguimiento Integral</h2>
                      <p className="text-gray-600 dark:text-gray-400">Versión 2.1.0</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Acerca de la aplicación</h3>
                        </div>
                      <div className="p-6 space-y-4">
                        <p className="text-gray-700 dark:text-gray-300">
                          El Sistema de Seguimiento Integral es una plataforma diseñada para facilitar el seguimiento del desarrollo de habilidades estudiantiles en instituciones educativas.
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">
                          Nuestra herramienta permite a los docentes registrar, monitorear y analizar el progreso de los estudiantes en diferentes dimensiones académicas, generando informes detallados y visualizaciones gráficas.
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">
                          Desarrollado en colaboración con equipos pedagógicos para asegurar su alineación con proyectos educativos institucionales.
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Contacto</h3>
                        </div>
                        <div className="p-6 space-y-2">
                          <p className="text-gray-700 dark:text-gray-300 flex items-center">
                            <FaEnvelope className="mr-2 text-gray-500 dark:text-gray-400" />
                            soporte@colegiopuertovaras.cl
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 flex items-center">
                            <FaPhone className="mr-2 text-gray-500 dark:text-gray-400" />
                            +56 9 1234 5678
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 flex items-center">
                            <FaGlobe className="mr-2 text-gray-500 dark:text-gray-400" />
                            www.colegiopuertovaras.cl
                          </p>
                      </div>
                    </div>

                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Información técnica</h3>
                        </div>
                        <div className="p-6 space-y-4">
                          <p className="text-gray-700 dark:text-gray-300">
                            <span className="font-semibold">Tecnologías:</span> React, Node.js, MongoDB
                          </p>
                          <p className="text-gray-700 dark:text-gray-300">
                            <span className="font-semibold">Desarrollador:</span> Equipo de Desarrollo CPV
                          </p>
                          <p className="text-gray-700 dark:text-gray-300">
                            <span className="font-semibold">Lanzamiento:</span> Febrero 2023
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                      © {new Date().getFullYear()} Colegio Sin Notas. Todos los derechos reservados.
                    </div>
                  </div>
                ) : activeHelpTab === 'status' ? (
                  // Contenido de Estado del Sistema
                  <div className="space-y-6">
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      Información en tiempo real sobre el estado de conexión y funcionamiento del sistema:
                    </p>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                          <FaWifi className="mr-2 text-green-500" />
                          Estado de Conexión
                        </h3>
                      </div>
                      <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Estado actual:</span>
                          <span className="flex items-center font-medium text-green-600 dark:text-green-400">
                            <div className="w-3 h-3 rounded-full mr-2 bg-green-400 animate-pulse"></div>
                            Conectado (HTTP)
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Servidor:</span>
                          <span className="text-gray-800 dark:text-gray-200 font-mono text-sm">{API_BASE_URL}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Autenticado:</span>
                          <span className={`font-medium ${authenticated ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {authenticated ? 'Sí' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Protocolo:</span>
                          <span className="text-gray-800 dark:text-gray-200">WebSocket</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Última actualización:</span>
                          <span className="text-gray-800 dark:text-gray-200">{new Date().toLocaleTimeString()}</span>
                        </div>
                        
                        {/* HTTP-only mode always connected - no reconnection needed */}
                        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
                          <p className="text-green-700 dark:text-green-300 text-sm">
                            ✅ Sistema optimizado para coordinación HTTP. Funcionamiento estable garantizado.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                          <FaCog className="mr-2 text-blue-500" />
                          Información del Sistema
                        </h3>
                      </div>
                      <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Usuario actual:</span>
                          <span className="text-gray-800 dark:text-gray-200 font-medium">{username}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Rol:</span>
                          <span className="text-gray-800 dark:text-gray-200 font-medium capitalize">{role}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Cambios sin guardar:</span>
                          <span className={`font-medium ${unsavedChanges ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                            {unsavedChanges ? 'Sí' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Actualizaciones disponibles:</span>
                          <span className={`font-medium ${updatesAvailable ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            {updatesAvailable ? 'Sí' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Nueva sección de Novedades y Actualizaciones
                  <div className="space-y-6">
                    <p className="text-gray-700 dark:text-gray-300 text-lg">
                      Manténgase al día con las últimas mejoras y actualizaciones de nuestra plataforma:
                    </p>
                    
                    <div className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800/50 shadow-lg overflow-hidden">
                      <div className="flex items-center justify-between px-6 py-4 border-b border-amber-100 dark:border-amber-800/30">
                        <div className="flex items-center">
                          <div className="bg-amber-100 dark:bg-amber-800/50 p-3 rounded-full mr-3">
                            <FaSyncAlt className="text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                              Sistema de Sincronización Mejorado
                              <span className="ml-3 text-xs bg-amber-500 text-white dark:bg-amber-600 dark:text-amber-50 font-mono px-2 py-0.5 rounded-full">
                                v2.1.0
                              </span>
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">26 de Julio, 2024</p>
                          </div>
                        </div>
                        <div className="hidden md:block">
                          <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 rounded-full">
                            Más reciente
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300">
                          <p className="mb-4">
                            Se ha implementado un sistema de sincronización en tiempo real significativamente optimizado que permite:
                          </p>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <span className="bg-green-100 dark:bg-green-800/50 p-1 rounded-full mr-2 mt-1">
                                <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                              </span>
                              <span><strong>Detección inteligente de cambios:</strong> El sistema ahora detecta con mayor eficiencia los cambios realizados por otros usuarios, mejorando la colaboración en tiempo real.</span>
                        </li>
                            <li className="flex items-start">
                              <span className="bg-green-100 dark:bg-green-800/50 p-1 rounded-full mr-2 mt-1">
                                <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                              </span>
                              <span><strong>Protección avanzada contra sobrescritura:</strong> Implementación de un sistema de bloqueo que previene de manera más robusta la sobrescritura del trabajo de otros docentes.</span>
                            </li>
                            <li className="flex items-start">
                              <span className="bg-green-100 dark:bg-green-800/50 p-1 rounded-full mr-2 mt-1">
                                <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                </svg>
                              </span>
                              <span><strong>Notificaciones en tiempo real:</strong> El sistema ahora envía notificaciones instantáneas cuando hay actualizaciones disponibles, manteniendo a todos los usuarios informados.</span>
                            </li>
                            <li className="flex items-start">
                              <span className="bg-green-100 dark:bg-green-800/50 p-1 rounded-full mr-2 mt-1">
                                <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                </svg>
                              </span>
                              <span><strong>Sincronización basada en deltas:</strong> Ahora el sistema guarda únicamente los cambios realizados (deltas), reduciendo la carga de datos y mejorando la velocidad de sincronización.</span>
                            </li>
                            <li className="flex items-start">
                              <span className="bg-green-100 dark:bg-green-800/50 p-1 rounded-full mr-2 mt-1">
                                <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                </svg>
                              </span>
                              <span><strong>Resolución de conflictos:</strong> Implementación de un sistema mejorado para la resolución de conflictos en ediciones concurrentes, facilitando el trabajo colaborativo.</span>
                        </li>
                      </ul>
                    
                          <div className="mt-6 bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border-l-4 border-blue-500 dark:border-blue-600">
                            <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-1">¿Cómo probarlo?</h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              Para experimentar la sincronización mejorada, simplemente abra la aplicación en dos navegadores diferentes o dispositivos, inicie sesión con cuentas diferentes, y realice cambios en los mismos estudiantes desde ambas sesiones.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/50 shadow-lg overflow-hidden opacity-60">
                      <div className="flex items-center justify-between px-6 py-4 border-b border-indigo-100 dark:border-indigo-800/30">
                        <div className="flex items-center">
                          <div className="bg-indigo-100 dark:bg-indigo-800/50 p-3 rounded-full mr-3">
                            <FaChartPie className="text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                              Próximamente: Nuevas Visualizaciones
                              <span className="ml-3 text-xs bg-indigo-500 text-white dark:bg-indigo-600 dark:text-indigo-50 font-mono px-2 py-0.5 rounded-full">
                                v2.2.0
                              </span>
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Planificado para Agosto, 2024</p>
                          </div>
                        </div>
                        <div className="hidden md:block">
                          <span className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 rounded-full">
                            Próximamente
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer del modal */}
              <div className="border-t border-gray-200 dark:border-gray-700 p-5 bg-gray-50 dark:bg-gray-800 rounded-b-xl flex justify-between items-center">
                <button 
                  onClick={() => {
                    if (activeHelpTab === 'instructions') setActiveHelpTab('about');
                    else if (activeHelpTab === 'about') setActiveHelpTab('updates');
                    else setActiveHelpTab('instructions');
                  }}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center transition-colors"
                >
                  {activeHelpTab === 'instructions' ? (
                    <>
                      <FaInfoCircle className="mr-2" /> 
                      Ver información de la app
                    </>
                  ) : activeHelpTab === 'about' ? (
                    <>
                      <FaBell className="mr-2" /> 
                      Ver novedades y actualizaciones
                    </>
                  ) : (
                    <>
                      <FaQuestionCircle className="mr-2" /> 
                      Ver instrucciones de uso
                    </>
                  )}
                </button>
                
                <div>
                  <button 
                    onClick={() => setShowHelpModal(false)}
                    className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white px-5 py-2 rounded-lg transition-colors duration-150 font-medium shadow-sm hover:shadow"
                  >
                    Entendido
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 🎯 MODAL DE PROGRESO GRANULAR */}
        <SaveProgressModal
          isVisible={showSaveProgressModal}
          onComplete={handleSaveProgressComplete}
          onError={handleSaveProgressError}
          studentsData={Object.values(granularChanges)}
        />
        </div>
      </SocketProvider>
    </TenantProvider>
  );
};

// ✅ Usando el SocketProvider real de contexts/SocketContext con coordinación completa

export default StudentSkillsTrackingSystem;














